// Import types but not apiRequest (we'll add our own)
import {
  FieldMapping,
  ChoiceMapping,
  SyncModeStatistics,
  CreateFieldMappingRequest,
  CreateChoiceMappingRequest,
  DiscoverMappingsRequest,
  UpdateSyncModeRequest,
  ApiResponse,
  DiscoveryResponse,
  ApplyDiscoveryRequest,
  UpdateSyncModeResponse,
  AllMappingsResponse,
  SyncMode,
  SyncLogsResponse,
  EntityMapping,
  EntityMappingV2,
  CreateEntityMappingRequest,
  UpdateEntityMappingRequest,
  EntityMappingResponse,
  DataverseEntityInfo,
  DataverseFieldInfo,
  TaskResponse,
  ProcessEntityRecordsParams,
  BatchProcessResponse,
  TaskLogsResponse,
  RelationshipInfo
} from "@shared/api-types";
import { msalInstance } from "./msalInstance";
import { loginRequest } from "./msalConfig";

// Circuit breaker to prevent hammering a down API
class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime = 0;
  private state: 'CLOSED' | 'OPEN' | 'HALF_OPEN' = 'CLOSED';
  private readonly failureThreshold = 3;
  private readonly resetTimeout = 30000; // 30 seconds

  isOpen(): boolean {
    if (this.state === 'OPEN') {
      const now = Date.now();
      if (now - this.lastFailureTime > this.resetTimeout) {
        console.log('[CircuitBreaker] Transitioning to HALF_OPEN state');
        this.state = 'HALF_OPEN';
        return false;
      }
      return true;
    }
    return false;
  }

  recordSuccess(): void {
    if (this.state === 'HALF_OPEN') {
      console.log('[CircuitBreaker] Request succeeded in HALF_OPEN, resetting to CLOSED');
      this.reset();
    }
  }

  recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.failureThreshold && this.state === 'CLOSED') {
      console.warn(`[CircuitBreaker] Opening circuit after ${this.failureCount} failures. Will retry after ${this.resetTimeout}ms`);
      this.state = 'OPEN';
    } else if (this.state === 'HALF_OPEN') {
      console.warn('[CircuitBreaker] Request failed in HALF_OPEN, reopening circuit');
      this.state = 'OPEN';
    }
  }

  reset(): void {
    this.failureCount = 0;
    this.lastFailureTime = 0;
    this.state = 'CLOSED';
  }
}

const circuitBreaker = new CircuitBreaker();

// Super simple URL builder - just join base URL with /api/ prefix and the path
export function buildApiUrl(path: string): string {
  // Get the base URL from the environment variable defined at build time
  // Make sure we have a default for local development
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:7072";

  // console.log(`[API] Environment VITE_API_BASE_URL: ${import.meta.env.VITE_API_BASE_URL}`);
  // console.log(`[API] Using base URL: ${baseUrl} for path: ${path}`);
  return `${baseUrl}${path}`;
}

// Helper function for making API requests with enhanced debugging and URL handling
export async function apiRequest(
  method: string,
  path: string,
  body?: any
): Promise<Response> {
  // Skip OAuth authentication for local development since API functions use authLevel: 'anonymous'
  // In production, Static Web Apps will handle authentication automatically
  const isLocalDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  let token: string | null = null;

  if (!isLocalDevelopment) {
    // Only attempt OAuth in production
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const result = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        token = result.accessToken;
      }
    } catch (error) {
      console.warn(`[AUTH] Could not get token for production (${path}):`, error);
    }
  }

  const options: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  // Add auth token only if we have one (production scenario)
  if (token) {
    (options.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // In local development, add x-ms-client-principal header using real MSAL account if available
  if (isLocalDevelopment) {
    const accounts = msalInstance.getAllAccounts();
    const account = accounts[0];

    // Use real account info if available, otherwise fall back to mock
    const principal = account ? {
      identityProvider: 'aad',
      userId: account.localAccountId, // This is the Azure AD Object ID
      userDetails: account.username,  // This is the email/UPN
      userRoles: ['authenticated', 'admin']
    } : {
      identityProvider: 'aad',
      userId: 'dev-user-id',
      userDetails: 'dev@example.com',
      userRoles: ['authenticated', 'admin']
    };

    const encodedPrincipal = btoa(JSON.stringify(principal));
    (options.headers as Record<string, string>)['x-ms-client-principal'] = encodedPrincipal;
  }

  // Use our simplified URL builder
  const url = buildApiUrl(path);

  // Check circuit breaker before making request
  if (circuitBreaker.isOpen()) {
    const error = new Error('API is temporarily unavailable. Circuit breaker is open. Retrying shortly...');
    error.name = 'CircuitBreakerOpen';
    throw error;
  }

  try {
    // console.log(`[API] Making ${method} request to: ${url}`);
    // console.log(`[API] Headers:`, options.headers);
    const response = await fetch(url, options);

    if (response.status === 401 || response.status === 403) {
      console.warn(`Auth error ${response.status} when calling API: ${url}`);
    }

    // Record success for circuit breaker
    if (response.ok) {
      circuitBreaker.recordSuccess();
    }

    return response;
  } catch (error) {
    // Only record failure for network errors (not HTTP errors)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      circuitBreaker.recordFailure();
      console.error(`API connection error for ${url}. Circuit breaker recorded failure.`);
    } else {
      console.error(`API request error for ${url}:`, error);
    }
    throw error;
  }
}

// Field mapping API calls
export const fetchAllMappings = async (): Promise<AllMappingsResponse> => {
  console.log("Client: fetchAllMappings called");
  try {
    // Changed from /api/field-mappings to /api/field-mappings to match queryKey and server endpoint
    const res = await apiRequest("GET", "/api/field-mappings");
    const data = await res.json();
    console.log("Client: fetchAllMappings response:", {
      status: res.status,
      dataStructure: data ? Object.keys(data) : "No data",
      fieldMappingsCount: data?.fieldMappings?.length || 0,
      choiceMappingsCount: data?.choiceMappings?.length || 0,
    });
    return data;
  } catch (error) {
    console.error("Client: Error in fetchAllMappings:", error);
    throw error;
  }
};

export const fetchFieldMappings = async (
  entityName?: string,
  syncMode?: SyncMode,
): Promise<FieldMapping[]> => {
  // Use the combined endpoint with type parameter
  let url = "/api/field-mappings";
  const params = new URLSearchParams();

  // This directs the server to return only field mappings
  params.append("type", "field");
  if (entityName) params.append("entityName", entityName);
  if (syncMode) params.append("syncMode", syncMode);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  console.log(`Client: Fetching field mappings from: ${url}`);
  const res = await apiRequest("GET", url);
  const data = await res.json();

  if (Array.isArray(data)) {
    return data;
  }

  if (data && Array.isArray(data.fieldMappings)) {
    return data.fieldMappings;
  }

  console.warn("Client: unexpected field mappings response shape", data);
  return [];
};

export const createFieldMapping = async (
  mapping: CreateFieldMappingRequest,
): Promise<ApiResponse> => {
  const res = await apiRequest("POST", "/api/field-mappings", mapping);
  return res.json();
};

export const deleteMapping = async (
  partitionKey: string,
  rowKey: string,
): Promise<ApiResponse> => {
  console.log("Client: Starting deleteMapping call with params:", {
    partitionKey,
    rowKey,
  });

  
  const url = `/api/field-mappings?partitionKey=${encodeURIComponent(partitionKey)}&rowKey=${encodeURIComponent(rowKey)}`;

  console.log("Client: Sending DELETE request to:", url);

  try {
    const res = await apiRequest("DELETE", url);
    console.log("Client: Delete response status:", res.status);
    const jsonResponse = await res.json();
    console.log("Client: Delete response data:", jsonResponse);
    return jsonResponse;
  } catch (error) {
    console.error("Client: Error in deleteMapping:", error);
    throw error;
  }
};

// Choice mapping API calls
export const fetchChoiceMappings = async (
  entityName?: string,
  fieldName?: string,
): Promise<ChoiceMapping[]> => {
  // Use the dedicated choice-mappings endpoint
  const params = new URLSearchParams();
  params.append("partitionKey", "ChoiceMapping");

  // Add query parameters if they exist
  if (entityName) params.append("entityName", entityName);
  if (fieldName) params.append("fieldName", fieldName);

  const url = `/api/field-mappings?${params.toString()}`;

  try {
    // Call the dedicated endpoint
    console.log(`Client: Fetching choice mappings from: ${url}`);
    const res = await apiRequest("GET", url);
    const data = await res.json();

    // Handle response based on the structure
    if (Array.isArray(data)) {
      return data.map(mapping => {
        // Start with the original mapping object
        const baseMapping = { ...mapping };
        
        // Ensure required properties have default values if missing
        if (!baseMapping.partitionKey) baseMapping.partitionKey = "";
        if (!baseMapping.rowKey) baseMapping.rowKey = "";
        if (!baseMapping.timestamp) baseMapping.timestamp = new Date().toISOString();
        if (!baseMapping.entityName) baseMapping.entityName = "";
        if (!baseMapping.fieldName) baseMapping.fieldName = "";
        if (!baseMapping.kupValue) baseMapping.kupValue = "";
        if (!baseMapping.hsValue) baseMapping.hsValue = "";
        if (!baseMapping.hsLabel) baseMapping.hsLabel = "";
        if (!baseMapping.etag) baseMapping.etag = "";
        
        return baseMapping as ChoiceMapping;
      });
    } else if (data && Array.isArray(data.choiceMappings)) {
      return data.choiceMappings;
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching choice mappings:", error);
    
    // If there's an error, return an empty array
    return [];
  }
};

export const createChoiceMapping = async (
  mapping: CreateChoiceMappingRequest,
): Promise<ApiResponse> => {
  // Choice mappings use the same endpoint as field mappings, with type: "choice"
  const res = await apiRequest("POST", "/api/field-mappings", mapping);
  return res.json();
};

// Statistics API calls
export const fetchSyncModeStats = async (
  entityName: string,
): Promise<SyncModeStatistics> => {
  // Use the dedicated statistics endpoint instead
  const url = `/api/statistics/${encodeURIComponent(entityName)}`;
  console.log("Fetching stats from:", url);
  const res = await apiRequest("GET", url);
  const data = await res.json();
  console.log("Stats API response:", data);
  return data;
};

// Auto-discovery API calls
export const discoverMappings = async (
  request: DiscoverMappingsRequest,
): Promise<DiscoveryResponse> => {
  console.log("Client: Starting discoverMappings with request:", request);
  try {
    const res = await apiRequest("POST", "/api/field-mappings", request);
    console.log("Client: Auto-discover response status:", res.status);
    const jsonResponse = await res.json();
    console.log("Client: Auto-discover response data:", jsonResponse);
    return jsonResponse;
  } catch (error) {
    console.error("Client: Error in discoverMappings:", error);
    throw error;
  }
};

export const applyDiscoveredMappings = async (
  request: ApplyDiscoveryRequest,
): Promise<DiscoveryResponse> => {
  const res = await apiRequest("POST", "/api/field-mappings", request);
  return res.json();
};

// Bulk update API calls
export const updateSyncMode = async (
  request: UpdateSyncModeRequest,
): Promise<UpdateSyncModeResponse> => {
  const res = await apiRequest("POST", "/api/update-sync-mode", request);
  return res.json();
};

// Azure Storage and Sync Logs API calls
export const checkAzureStorageStatus = async (): Promise<{
  success: boolean;
  configured: boolean;
  message: string;
}> => {
  const res = await apiRequest("GET", "/api/system/azure-storage-status");
  return res.json();
};

export const fetchSyncLogs = async (
  limit?: number,
): Promise<SyncLogsResponse> => {
  // Use the new queryCustomEvents function
  return await queryCustomEvents({
    eventName: "EntitySync",
    includeStatusCounts: true,
    limit: limit || 100
  });
};



export const fetchEntitySyncLogs = async (
  parentEntity?: string,
  childEntity?: string,
  status?: string,
  batchId?: string,
  startTime?: string,
  endTime?: string,
  lastSeenTimestamp?: string,
  lastSeenId?: string,
  searchTerm?: string,
  limit: number = 100
): Promise<SyncLogsResponse> => {
  // Build query parameters for the new API
  const params: any = {
    limit,
    includeStatusCounts: true
  };

  // Defensive: only allow valid entity names (alphanumeric, underscore, dash)
  const validParentEntity =
    parentEntity &&
    parentEntity !== "all" &&
    /^[a-zA-Z0-9_-]+$/.test(parentEntity)
      ? parentEntity
      : undefined;
  if (validParentEntity) {
    params.parentEntity = validParentEntity;
  }

  const validChildEntity =
    childEntity &&
    childEntity !== "all" &&
    /^[a-zA-Z0-9_-]+$/.test(childEntity)
      ? childEntity
      : undefined;
  if (validChildEntity) {
    params.childEntity = validChildEntity;
  }

  if (status && status !== "all") {
    params.status = status;
  }
  if (batchId && batchId !== "all") {
    params.batchId = batchId;
  }
  if (startTime) {
    params.startTime = startTime;
  }
  if (endTime) {
    params.endTime = endTime;
  }
  if (lastSeenTimestamp) {
    params.lastSeenTimestamp = lastSeenTimestamp;
  }
  if (lastSeenId) {
    params.lastSeenId = lastSeenId;
  }
  if (searchTerm) {
    params.searchTerm = searchTerm;
  }

  console.log("Entity Sync Logs - Request params:", {
    parentEntity,
    childEntity,
    status,
    batchId,
    startTime,
    endTime,
    lastSeenTimestamp,
    lastSeenId,
    limit,
    params
  });

  // Call the new queryCustomEvents function with entity mapping logs endpoint
  return await queryCustomEvents(params, true);
};

/**
 * Query custom events from Application Insights
 *
 * @param params Query parameters for filtering custom events
 * @param isEntityMapping If true, use the entity mapping logs endpoint
 * @returns Promise resolving to query results
 */
export const queryCustomEvents = async (
  params: {
    eventName?: string;
    parentEntity?: string;
    childEntity?: string;
    parentId?: string;
    childId?: string;
    success?: boolean;
    syncStatus?: string;
    batchId?: string;
    mappingId?: string;
    startTime?: string;
    endTime?: string;
    timeRange?: string; // Add timeRange parameter
    limit?: number;
    skip?: number;
    correlationId?: string;
    includeProcessingLog?: boolean;
    includeUpdatedFields?: boolean;
    includeStatusCounts?: boolean;
    lastSeenTimestamp?: string;
    lastSeenId?: string;
    status?: string; // Add status parameter to match fetchFieldSyncLogs
    searchTerm?: string;
  },
  isEntityMapping: boolean = false
): Promise<SyncLogsResponse> => {
  // Convert params to query string
  const queryParams = new URLSearchParams();

  // Add all parameters to the query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  // Use the correct endpoint for entity mapping logs or field mapping logs
  const url = isEntityMapping
    ? `/api/query-entity-mapping-logs?${queryParams.toString()}`
    : `/api/query-field-mapping-logs?${queryParams.toString()}`;

  console.log("CLIENT CUSTOM EVENTS REQUEST:", {
    url,
    params,
    isEntityMapping
  });

  try {
    const response = await apiRequest("GET", url);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Client: queryCustomEvents - Error response:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    console.log(
      `Client: queryCustomEvents - Retrieved ${data.logs.length} of ${data.totalCount} logs`
    );

    // Transform the response to match the expected SyncLogsResponse format
    return {
      logs: data.logs,
      totalCount: data.totalCount,
      statusCounts: data.statusCounts || {
        done: 0,
        error: 0,
        no_update_needed: 0,
        pending: 0,
        total: data.totalCount,
      }
    };
  } catch (error) {
    console.error("Client: Error in queryCustomEvents:", error);
    throw error;
  }
};

// Task API calls
export const executeTask = async (
  taskName: string,
  params?: Record<string, any>,
): Promise<TaskResponse> => {
  const response = await apiRequest("POST", "/api/tasks/execute", {
    taskName,
    params,
  });
  return await response.json();
};

export const processEntityRecords = async (
  params: ProcessEntityRecordsParams,
): Promise<BatchProcessResponse> => {
  const response = await apiRequest(
    "POST",
    "/api/management-tasks/start-process-entity-records",
    params,
  );
  return await response.json();
};

export const fetchTaskLogs = async (filter?: {
  status?: string;
  name?: string;
  limit?: number;
}): Promise<TaskLogsResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("tableName", "ManagementTasks");
  if (filter?.status) {
    queryParams.append("status", filter.status);
  }

  if (filter?.name) {
    queryParams.append("name", filter.name);
  }

  if (filter?.limit) {
    queryParams.append("limit", filter.limit.toString());
  }

  const url = `/api/table-logs?${queryParams.toString()}`;
  const response = await apiRequest("GET", url);
  const data = await response.json();

  // Transform the response to match TaskLogsResponse format
  const logs = data.logs || [];

  // Calculate status counts from the logs
  const statusCounts = logs.reduce((counts: any, log: any) => {
    const status = log.status;
    if (status === "pending") counts.pending++;
    else if (status === "running") counts.running++;
    else if (status === "completed") counts.completed++;
    else if (status === "error") counts.failed++;
    counts.total++;
    return counts;
  }, { pending: 0, running: 0, completed: 0, failed: 0, total: 0 });

  return {
    success: true,
    logs,
    statusCounts
  };
};

// Cancel a running task
export const cancelTask = async (taskId: string): Promise<{ success: boolean; message?: string; error?: string }> => {
  const url = `/api/tasks/${taskId}/cancel`;
  const response = await apiRequest("POST", url);
  return await response.json();
};

// Field sync logs function
export const fetchFieldSyncLogs = async (
  parentEntity?: string,
  status?: string,
  batchId?: string,
  limit: number = 100
): Promise<SyncLogsResponse> => {
  return await queryCustomEvents({
    eventName: "FieldSync",
    parentEntity: parentEntity && parentEntity !== "all" ? parentEntity : undefined,
    syncStatus: status && status !== "all" ? status : undefined,
    batchId,
    includeStatusCounts: true,
    limit
  }, false); // false for field mapping logs
};

// Auto match fields function
export const autoMatchFields = async (
  entityName: string,
  mappingType?: string
): Promise<any> => {
  const res = await apiRequest("POST", "/api/field-mappings/auto-match", {
    entityName,
    mappingType
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to auto-match fields");
  }
  return res.json();
};

// API Rate Limit Status
export interface RateLimitStatus {
  availableTokens: number;
  maxTokens: number;
  currentRate: number;
  percentAvailable: number;
  timestamp: string;
}

export interface RateLimitResponse {
  success: boolean;
  data?: RateLimitStatus;
  error?: string;
}

/**
 * Fetch the current status of Dataverse API rate limits
 * @returns Promise with rate limit status data
 */
export const getRateLimitStatus = async (): Promise<RateLimitResponse> => {
  try {
    // Use our new apiRequest helper for consistent URL handling
    const response = await apiRequest("GET", "/api/management-tasks/rate-limits");
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      return {
        success: false,
        error: text || `Failed to fetch rate limit status (HTTP ${response.status})`,
      };
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching rate limit status:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching rate limit status'
    };
  }
};

// Rate Limit Health (simplified 1 token = 1 batch model)
export interface RateLimitHealth {
  currentRate: number;           // Current token consumption rate
  maxRate: number;               // Max tokens/sec (refill rate)
  availableTokens: number;       // Tokens currently in bucket
  maxTokens: number;             // Burst limit (bucket capacity)
  loadPercent: number;           // % of rate limit in use
  status: 'healthy' | 'elevated' | 'critical';
  avgMsPerRecord: number;        // Average ms per record from recent batches
  avgRecordsPerSecond: number;   // Throughput from recent batches
  tokenCostPerBatch: number;     // Fixed cost (always 1)
  batchSize: number;             // Fixed batch size (250)
  configSource: 'redis' | 'env' | 'default';

  // Backward-compatible aliases (older UI/endpoint variants)
  availableSlots?: number;
  maxConcurrentBatches?: number;
  batchesPerSecond?: number;
  maxBatchesPerSecond?: number;
  slotCostPerBatch?: number;
}

// Rate limit configuration (stored in Redis, with env var fallback)
export interface RateLimitConfig {
  rateLimitPerSecond: number;
  burstLimit: number;
  source: 'redis' | 'env' | 'default';
}

export interface RateLimitConfigResponse {
  success: boolean;
  config?: RateLimitConfig;
  message?: string;
  timestamp?: string;
  error?: string;
}

export interface BatchTimingMetric {
  timestamp: number;
  entityType: string;
  recordCount: number;
  dataverseDurationMs: number;
  msPerRecord: number;
  tokenCostUsed: number;
}

// Queue worker tracking types
export interface QueueWorkerInfo {
  workerId: string;
  entityType: string;
  messageType: string;
  startedAt: number;
}

export interface QueueWorkerStatus {
  batchSize: number;              // From host.json (6)
  activeWorkerCount: number;
  activeWorkers: QueueWorkerInfo[];
}

// SharePoint rate limit types
export interface SharePointRateLimitHealth {
  availableTokens: number;
  maxTokens: number;
  currentRate: number;
  maxRate: number;
  loadPercent: number;
  status: 'healthy' | 'elevated' | 'critical';
  configSource: 'redis' | 'env' | 'default';
  lastUpdated: string;
}

export interface SharePointRateLimitConfig {
  requestsPerSecond: number;
  maxConcurrentRequests: number;
  source: 'redis' | 'env' | 'default';
}

export interface SharePointRateLimitConfigResponse {
  success: boolean;
  config?: SharePointRateLimitConfig;
  message?: string;
  timestamp?: string;
  error?: string;
}

export interface RateLimitHealthResponse {
  success: boolean;
  health?: RateLimitHealth;
  sharePointHealth?: SharePointRateLimitHealth;
  summary?: string;
  recentBatches?: BatchTimingMetric[];
  queueWorkers?: QueueWorkerStatus;
  timestamp?: string;
  error?: string;
}

/**
 * Fetch enhanced rate limit health with dynamic costing metrics
 * @returns Promise with rate limit health data including batch timing metrics
 */
export const getRateLimitHealth = async (): Promise<RateLimitHealthResponse> => {
  try {
    const response = await apiRequest("GET", "/api/sql-sync/rate-limit-health");
    return await response.json();
  } catch (error) {
    console.error('Error fetching rate limit health:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching rate limit health'
    };
  }
};

/**
 * Get current rate limit configuration
 * @returns Promise with rate limit config (from Redis, env vars, or defaults)
 */
export const getRateLimitConfig = async (): Promise<RateLimitConfigResponse> => {
  try {
    const response = await apiRequest("GET", "/api/sql-sync/rate-limit-config");
    return await response.json();
  } catch (error) {
    console.error('Error fetching rate limit config:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching rate limit config'
    };
  }
};

/**
 * Update rate limit configuration (stored in Redis)
 * @param rateLimitPerSecond - Requests per second limit
 * @param burstLimit - Maximum burst size
 * @returns Promise with updated config
 */
export const updateRateLimitConfig = async (
  rateLimitPerSecond: number,
  burstLimit: number
): Promise<RateLimitConfigResponse> => {
  try {
    const response = await apiRequest("PUT", "/api/sql-sync/rate-limit-config", {
      rateLimitPerSecond,
      burstLimit
    });
    return await response.json();
  } catch (error) {
    console.error('Error updating rate limit config:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error updating rate limit config'
    };
  }
};

/**
 * Reset rate limit configuration to environment variable defaults
 * Removes Redis overrides
 * @returns Promise with reset config
 */
export const resetRateLimitConfig = async (): Promise<RateLimitConfigResponse> => {
  try {
    const response = await apiRequest("DELETE", "/api/sql-sync/rate-limit-config");
    return await response.json();
  } catch (error) {
    console.error('Error resetting rate limit config:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error resetting rate limit config'
    };
  }
};

// SharePoint rate limit API functions
export const getSharePointRateLimitConfig = async (): Promise<SharePointRateLimitConfigResponse> => {
  try {
    const response = await apiRequest("GET", "/api/sql-sync/sharepoint-rate-limit-config");
    return await response.json();
  } catch (error) {
    console.error('Error fetching SharePoint rate limit config:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching SharePoint rate limit config'
    };
  }
};

export const updateSharePointRateLimitConfig = async (
  requestsPerSecond: number,
  maxConcurrentRequests: number
): Promise<SharePointRateLimitConfigResponse> => {
  try {
    const response = await apiRequest("PUT", "/api/sql-sync/sharepoint-rate-limit-config", {
      requestsPerSecond,
      maxConcurrentRequests
    });
    return await response.json();
  } catch (error) {
    console.error('Error updating SharePoint rate limit config:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error updating SharePoint rate limit config'
    };
  }
};

export const resetSharePointRateLimitConfig = async (): Promise<SharePointRateLimitConfigResponse> => {
  try {
    const response = await apiRequest("DELETE", "/api/sql-sync/sharepoint-rate-limit-config");
    return await response.json();
  } catch (error) {
    console.error('Error resetting SharePoint rate limit config:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error resetting SharePoint rate limit config'
    };
  }
};

// Start filtered data retrieval job
export async function startFilteredDataRetrieval(entity: string, filter: any) {
  const res = await apiRequest("POST", "/api/dataverse/filtered-retrieval-job", { entity, filter });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to start data retrieval job");
  }
  return res.json();
}

// Entity mapping API calls
export const fetchEntityMappings = async (): Promise<EntityMapping[]> => {
  // Fetch all entity mappings from the API
  const res = await apiRequest("GET", "/api/entity-mappings");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch entity mappings");
  }
  // The API should return an array of EntityMapping objects
  return res.json();
};

export const fetchEntityMapping = async (rowKey: string): Promise<EntityMapping> => {
  // Fetch a single entity mapping by rowKey using query parameter
  const res = await apiRequest("GET", `/api/entity-mappings?entityMappingId=${encodeURIComponent(rowKey)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch entity mapping");
  }
  const mappings = await res.json();
  // The API returns an array even for single mapping queries
  if (!Array.isArray(mappings) || mappings.length === 0) {
    throw new Error("Entity mapping not found");
  }
  return mappings[0];
};

export const deleteEntityMapping = async (
  parentEntity: string,
  childEntity: string,
  parentLookupField: string,
  childLookupField: string
): Promise<{ success: boolean; message?: string }> => {
  // Build the query string for identifying the mapping
  const params = new URLSearchParams({
    parentEntity,
    childEntity,
    parentLookupField,
    childLookupField,
  });
  const url = `/api/entity-mappings?${params.toString()}`;
  const res = await apiRequest("DELETE", url);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to delete entity mapping");
  }
  return res.json();
};

// Create a new entity mapping
export const createEntityMapping = async (
  data: CreateEntityMappingRequest
): Promise<EntityMappingResponse> => {
  const res = await apiRequest("POST", "/api/entity-mappings", data);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to create entity mapping");
  }
  return res.json();
};

// Update an existing entity mapping
export const updateEntityMapping = async (
  data: UpdateEntityMappingRequest
): Promise<EntityMappingResponse> => {
  const res = await apiRequest("PATCH", "/api/entity-mappings", data);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to update entity mapping");
  }
  return res.json();
};

// V2 Entity Mapping API Functions
export const createEntityMappingV2 = async (
  data: Omit<EntityMappingV2, 'mappingId' | 'partitionKey' | 'rowKey' | 'timestamp' | 'etag'>
): Promise<EntityMappingV2> => {
  const res = await apiRequest("POST", "/api/v2/entity-mappings", data);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to create V2 entity mapping");
  }
  const response = await res.json();
  return response.mapping;
};

export const updateEntityMappingV2 = async (
  data: Omit<EntityMappingV2, 'partitionKey' | 'rowKey' | 'timestamp' | 'etag'>
): Promise<EntityMappingV2> => {
  const res = await apiRequest("PATCH", "/api/v2/entity-mappings", { ...data, mappingId: data.mappingId });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to update V2 entity mapping");
  }
  const response = await res.json();
  return response.mapping;
};

export const fetchEntityMappingV2 = async (mappingId: string): Promise<EntityMappingV2> => {
  const res = await apiRequest("GET", `/api/v2/entity-mappings?mappingId=${encodeURIComponent(mappingId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch V2 entity mapping");
  }
  const response = await res.json();
  return response.mapping;
};

export const fetchEntityMappingsV2 = async (): Promise<EntityMappingV2[]> => {
  const res = await apiRequest("GET", "/api/v2/entity-mappings");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch V2 entity mappings");
  }
  return res.json();
};

export const deleteEntityMappingV2 = async (mappingId: string): Promise<{ success: boolean; message?: string }> => {
  const res = await apiRequest("DELETE", `/api/v2/entity-mappings?mappingId=${encodeURIComponent(mappingId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to delete V2 entity mapping");
  }
  const response = await res.json();
  return { success: response.success, message: response.message };
};

// Clone entity mapping with optional mode conversion and direction reversal
export const cloneEntityMapping = async (params: {
  sourceMappingId: string;
  newName: string;
  targetMode?: string;
  reverseDirection?: boolean;
  copyFilters?: boolean;
  copyFieldMappings?: boolean;
  copyDateFilter?: boolean;
}): Promise<{
  success: boolean;
  mapping?: EntityMappingV2;
  warnings?: string[];
  errors?: string[];
  message?: string;
}> => {
  const res = await apiRequest("POST", "/api/cloneEntityMapping", params);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || error?.errors?.[0] || "Failed to clone entity mapping");
  }
  return res.json();
};

// Fetch all Dataverse entities for mapping
export const fetchDataverseEntities = async (filter?: string): Promise<DataverseEntityInfo[]> => {
  const params = filter ? `?${new URLSearchParams({ filter }).toString()}` : "";
  const res = await apiRequest("GET", `/api/entity-mappings/entities${params}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch Dataverse entities");
  }
  // The API returns an array of DataverseEntityInfo
  return res.json();
};

// Fetch entity metadata including entity set name
export const fetchEntityMetadata = async (entityName: string): Promise<{ logicalName: string; entitySetName: string }> => {
  if (!entityName) {
    throw new Error("Entity name is required");
  }
  const params = new URLSearchParams({ entityName });
  const url = `/api/entity-mappings/entity-metadata?${params.toString()}`;
  const res = await apiRequest("GET", url);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch entity metadata");
  }
  return res.json();
};

// Fetch all fields for a given entity (optionally exclude built-in fields)
export const fetchEntityFields = async (
  entityName: string
): Promise<DataverseFieldInfo[]> => {
  if (!entityName) return [];
  const params = new URLSearchParams({ entityName });
  const url = `/api/entity-mappings/fields?${params.toString()}`;
  const res = await apiRequest("GET", url);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch entity fields");
  }
  // The API returns an array of DataverseFieldInfo
  return res.json();
};

// Fetch entity lookup fields from the API
export const fetchEntityLookupFields = async (
  entityName: string
): Promise<DataverseFieldInfo[]> => {
  if (!entityName) return [];
  const params = new URLSearchParams({ entityName });
  const url = `/api/entity-mappings/lookupFields?${params.toString()}`;
  const res = await apiRequest("GET", url);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch entity lookup fields");
  }
  // The API returns an array of DataverseFieldInfo
  return res.json();
};

// Fetch option set values for a specific field
export const fetchOptionSetValues = async (
  entityName: string,
  fieldName: string
): Promise<Record<number, string>> => {
  if (!entityName || !fieldName) return {};
  const params = new URLSearchParams({ entityName, fieldName });
  const url = `/api/entity-mappings/optionSetValues?${params.toString()}`;
  const res = await apiRequest("GET", url);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch option set values");
  }
  // The API returns a Record<number, string> mapping option values to labels
  return res.json();
};

// Fetch relationships between two entities
export const fetchEntityRelationships = async (
  parentEntity: string,
  childEntity: string
): Promise<RelationshipInfo[]> => {
  if (!parentEntity || !childEntity) return [];

  const fetchSingleDirection = async (parent: string, child: string): Promise<RelationshipInfo[]> => {
    const params = new URLSearchParams({ parentEntity: parent, childEntity: child });
    const url = `/api/entity-mappings/relationships?${params.toString()}`;
    const res = await apiRequest("GET", url);
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error?.message || "Failed to fetch entity relationships");
    }
    return res.json();
  };

  const [forward, reverse] = await Promise.all([
    fetchSingleDirection(parentEntity, childEntity),
    parentEntity !== childEntity ? fetchSingleDirection(childEntity, parentEntity) : Promise.resolve([])
  ]);

  const combined = [...forward, ...reverse];
  const seen = new Set<string>();

  return combined.filter(rel => {
    const key = `${rel.schemaName}|${rel.relationshipType}|${rel.referencingEntity}|${rel.navigationPropertyName}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

// Navigation Property response interface
interface NavigationPropertyResponse {
  LogicalName: string;
  RelationshipType: string;
  ReferencedEntity: string;
  SchemaName: string;
}

// Fetch navigation properties for an entity
export const fetchNavigationProperties = async (entityLogicalName: string): Promise<NavigationPropertyResponse[]> => {
  if (!entityLogicalName) return [];

  const params = new URLSearchParams({ entity: entityLogicalName });
  const url = `/api/navigation-properties?${params.toString()}`;

  try {
    const res = await apiRequest("GET", url);
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error?.message || "Failed to fetch navigation properties");
    }

    const data = await res.json();
    return data.navigationProperties || [];
  } catch (error) {
    console.error(`Error fetching navigation properties for ${entityLogicalName}:`, error);
    throw error;
  }
};

// Fetch complete entity metadata including fields, relationships, and navigation properties
export const fetchCompleteEntityMetadata = async (entityLogicalName: string): Promise<{
  fields: DataverseFieldInfo[];
  relationships: RelationshipInfo[];
  navigationProperties: NavigationPropertyResponse[];
}> => {
  if (!entityLogicalName) {
    return { fields: [], relationships: [], navigationProperties: [] };
  }

  const params = new URLSearchParams({ entity: entityLogicalName });
  const url = `/api/entity-metadata?${params.toString()}`;

  try {
    const res = await apiRequest("GET", url);
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error?.message || "Failed to fetch entity metadata");
    }

    const data = await res.json();
    return {
      fields: data.fields || [],
      relationships: data.relationships || [],
      navigationProperties: data.navigationProperties || []
    };
  } catch (error) {
    console.error(`Error fetching entity metadata for ${entityLogicalName}:`, error);
    throw error;
  }
};

// Count Dataverse records for an entity with a filter
export const countDataverseRecords = async (
  entityName: string,
  primaryIdField: string,
  filter: any,
  associationConfig?: {
    mode: string;
    skipExisting?: boolean;
    associationMethod?: 'lookup_update' | 'collection_add';
    navigationProperty?: string;
    lookupFieldName?: string;
    toEntity?: string;
  },
  modifiedDateFilter?: {
    enabled: boolean;
    field: string;
    mode: 'absolute' | 'relative' | 'scheduler';
    absoluteRange?: { from?: string; to?: string };
    relativeRange?: { value: number; unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' };
  }
): Promise<number | null> => {
  const res = await apiRequest("POST", "/api/dataverse/count-records", {
    entityName,
    primaryIdField,
    filter,
    associationConfig,
    modifiedDateFilter,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to count Dataverse records");
  }
  const data = await res.json();

  // Handle processing-stage operators that don't support counting
  if (data.countingNotSupported) {
    console.warn('[countDataverseRecords] Counting not supported:', data.reason);
    return null; // Return null to indicate count is not available
  }

  return data.count;
};

/**
 * Query entity mapping logs from Application Insights
 *
 * @param params Query parameters for filtering entity mapping logs
 * @returns Promise resolving to query results
 */
export const queryEntityMappingLogs = async (
  params: {
    parentEntity?: string;
    childEntity?: string;
    parentId?: string;
    childId?: string;
    syncStatus?: string;
    batchId?: string;
    mappingId?: string;
    startTime?: string;
    endTime?: string;
    limit?: number;
    skip?: number;
    includeStatusCounts?: boolean;
    searchTerm?: string;
  }
): Promise<SyncLogsResponse> => {
  // Convert params to query string
  const queryParams = new URLSearchParams();

  // Add all parameters to the query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });

  const url = `/api/query-entity-mapping-logs?${queryParams.toString()}`;

  console.log("CLIENT ENTITY MAPPING LOGS REQUEST:", {
    url,
    params
  });

  try {
    const response = await apiRequest("GET", url);

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Client: queryEntityMappingLogs - Error response:", errorData);
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    console.log(
      `Client: queryEntityMappingLogs - Retrieved ${data.logs.length} of ${data.totalCount} logs`
    );

    return {
      logs: data.logs,
      totalCount: data.totalCount,
      statusCounts: data.statusCounts || {
        done: 0,
        error: 0,
        no_update_needed: 0,
        pending: 0,
        total: data.totalCount,
      }
    };
  } catch (error) {
    console.error("Client: Error in queryEntityMappingLogs:", error);
    throw error;
  }
};

// ============================================================================
// Scheduled Tasks API
// ============================================================================

export interface ScheduledTask {
  id: string;
  name: string;
  description?: string;
  taskType: string;
  entityMapping?: string;
  entityMappingName?: string;
  recordLimit?: number;
  schedule?: ScheduleConfiguration;
  nextExecutionTime?: string;
  lastExecutionTime?: string;
  // executionHistory removed - query via getTaskExecutions API instead
  createdOn: string;
  completedOn?: string;
  // Job-based task fields
  jobType?: string;
  jobId?: string;
  parameters?: Record<string, unknown>;

  // Parent/child task hierarchy fields
  parentTaskId?: string;           // NULL = parent/standalone, set = child task
  executionOrder?: number;         // Order within parent (1, 2, 3...)
  onChildFailure?: 'stop' | 'skip' | 'retry';  // How to handle child failure
  childCount?: number;             // Number of children (for parent tasks)

  // Status and enabled flags
  isEnabled?: boolean;
  status?: string;                 // pending, running, succeeded, failed, disabled
  claimedBy?: string;              // ExecutionId that has claimed this task
  claimedAt?: string;              // When the task was claimed

  // Retry policy fields
  maxRetries?: number;
  retryDelaySeconds?: number;
  retryBackoffMultiplier?: number;
  currentRetryCount?: number;

  // Task groups/tags
  taskGroup?: string;
  tags?: string[];
}

export interface ScheduleConfiguration {
  enabled: boolean;
  cronExpression?: string;
  interval?: {
    value: number;
    unit: 'minutes' | 'hours' | 'days' | 'weeks';
  };
  startDate?: string;
  endDate?: string;
  timezone: string;
  maxRetries: number;
  retryDelayMinutes: number;
}

export interface TaskExecutionRecord {
  executionId: string;
  startTime: string;
  endTime?: string;
  status: 'succeeded' | 'failed' | 'running' | 'retrying';
  output?: any;
  error?: string;
}

export interface ScheduledTasksResponse {
  success: boolean;
  tasks: ScheduledTask[];
  count: number;
}

export interface ScheduledTaskResponse {
  success: boolean;
  task: ScheduledTask;
  message?: string;
}

export interface CreateScheduledTaskRequest {
  name: string;
  description?: string;
  // Entity mapping fields (optional for job-based tasks)
  entityMapping?: string;
  entityMappingName?: string;
  recordLimit?: number;
  schedule: ScheduleConfiguration;
  // Job-based task fields
  jobType?: string;
  jobId?: string;
  parameters?: Record<string, unknown>;
  // Parent/child hierarchy
  parentTaskId?: string;
  executionOrder?: number;
  onChildFailure?: 'stop' | 'skip' | 'retry';
  // Retry policy
  maxRetries?: number;
  retryDelaySeconds?: number;
  // Task groups
  taskGroup?: string;
  tags?: string[];
}

export interface UpdateScheduledTaskRequest {
  name?: string;
  description?: string;
  entityMapping?: string;
  entityMappingName?: string;
  recordLimit?: number;
  schedule?: ScheduleConfiguration;
  // Job-based task fields
  jobType?: string;
  jobId?: string;
  parameters?: Record<string, unknown>;
  // Parent/child hierarchy
  onChildFailure?: 'stop' | 'skip' | 'retry';
  // Retry policy
  maxRetries?: number;
  retryDelaySeconds?: number;
  // Task groups
  taskGroup?: string;
  tags?: string[];
}

// Job Type definitions for the scheduler UI
export interface JobTypeParameter {
  name: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'select' | 'json';
  required: boolean;
  description?: string;
  default?: string | number | boolean;
  options?: Array<{ value: string; label: string }>;
  optionsSource?: 'sqlSyncEntities' | 'fieldSyncEntities' | 'sharePointJobs' | 'sentimentPipelines';
}

export interface JobTypeDefinition {
  jobType: string;
  name: string;
  description: string;
  icon: string;
  category: 'sql' | 'sharepoint' | 'maintenance' | 'sentiment';
  parameters: JobTypeParameter[];
}

export interface SqlSyncEntityOption {
  value: string;
  label: string;
  enabled: boolean;
  description: string;
  entityLogicalName: string;
  entitySet: string;
  primaryKey: string;
  masterTable: string;
  capabilities: {
    sqlSync: boolean;
    metadataRefresh: boolean;
    guardian: boolean;
    fieldSync: boolean;
  };
}

export interface JobTypesResponse {
  success: boolean;
  jobTypes: JobTypeDefinition[];
  options: {
    sqlSyncEntities: SqlSyncEntityOption[];
    fieldSyncEntities: SqlSyncEntityOption[];
    sentimentPipelines: Array<{ value: string; label: string }>;
  };
}

/**
 * Get available job types for scheduled tasks
 */
export const getJobTypes = async (): Promise<JobTypesResponse> => {
  try {
    const response = await apiRequest("GET", "/api/scheduler/job-types");
    return await response.json();
  } catch (error) {
    console.error("Error getting job types:", error);
    throw error;
  }
};

/**
 * List all scheduled tasks
 */
export const listScheduledTasks = async (): Promise<ScheduledTasksResponse> => {
  try {
    const response = await apiRequest("GET", "/api/scheduledTasks");
    return await response.json();
  } catch (error) {
    console.error("Error listing scheduled tasks:", error);
    throw error;
  }
};

/**
 * Get a specific scheduled task by ID
 */
export const getScheduledTask = async (id: string): Promise<ScheduledTaskResponse> => {
  try {
    const response = await apiRequest("GET", `/api/scheduledTasks/${id}`);
    return await response.json();
  } catch (error) {
    console.error(`Error getting scheduled task ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new scheduled task
 */
export const createScheduledTask = async (
  data: CreateScheduledTaskRequest
): Promise<ScheduledTaskResponse> => {
  try {
    const response = await apiRequest("POST", "/api/scheduledTasks", data);
    return await response.json();
  } catch (error) {
    console.error("Error creating scheduled task:", error);
    throw error;
  }
};

/**
 * Update an existing scheduled task
 */
export const updateScheduledTask = async (
  id: string,
  data: UpdateScheduledTaskRequest
): Promise<ScheduledTaskResponse> => {
  try {
    const response = await apiRequest("PUT", `/api/scheduledTasks/${id}`, data);
    return await response.json();
  } catch (error) {
    console.error(`Error updating scheduled task ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a scheduled task
 */
export const deleteScheduledTask = async (id: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiRequest("DELETE", `/api/scheduledTasks/${id}`);
    return await response.json();
  } catch (error) {
    console.error(`Error deleting scheduled task ${id}:`, error);
    throw error;
  }
};

/**
 * Enable a scheduled task
 */
export const enableScheduledTask = async (id: string): Promise<ScheduledTaskResponse> => {
  try {
    const response = await apiRequest("POST", `/api/scheduledTasks/${id}/enable`);
    return await response.json();
  } catch (error) {
    console.error(`Error enabling scheduled task ${id}:`, error);
    throw error;
  }
};

/**
 * Disable a scheduled task
 */
export const disableScheduledTask = async (id: string): Promise<ScheduledTaskResponse> => {
  try {
    const response = await apiRequest("POST", `/api/scheduledTasks/${id}/disable`);
    return await response.json();
  } catch (error) {
    console.error(`Error disabling scheduled task ${id}:`, error);
    throw error;
  }
};

/**
 * Validate and repair schedule index for a task
 */
export const validateScheduleIndex = async (id: string): Promise<{
  success: boolean;
  valid: boolean;
  fixed: boolean;
  message: string;
  details?: any;
}> => {
  try {
    const response = await apiRequest("POST", `/api/scheduledTasks/${id}/validate`);
    return await response.json();
  } catch (error) {
    console.error(`Error validating schedule index for task ${id}:`, error);
    throw error;
  }
};

/**
 * Execute a scheduled task immediately (run now)
 */
export const executeScheduledTask = async (id: string): Promise<{
  success: boolean;
  message: string;
  jobId?: string;
  executionId?: string;
}> => {
  try {
    const response = await apiRequest("POST", `/api/scheduledTasks/${id}/execute`);
    return await response.json();
  } catch (error) {
    console.error(`Error executing scheduled task ${id}:`, error);
    throw error;
  }
};

/**
 * Cancel a stuck running scheduled task
 * Resets the task from 'running' status back to 'pending' and clears the claim
 */
export const cancelScheduledTask = async (id: string): Promise<ScheduledTaskResponse> => {
  try {
    const response = await apiRequest("POST", `/api/scheduledTasks/${id}/cancel`);
    return await response.json();
  } catch (error) {
    console.error(`Error cancelling scheduled task ${id}:`, error);
    throw error;
  }
};

// ========================================
// Task Execution APIs
// ========================================

export interface TaskExecution {
  partitionKey?: string;
  rowKey?: string;
  taskId: string;
  taskName: string;
  executionId: string;
  parentExecutionId?: string;  // Links child execution to parent execution
  startTime: string;
  endTime?: string | null;
  durationMs?: number | null;
  status: 'succeeded' | 'failed' | 'running' | 'skipped' | 'cancelled' | 'stopped' | 'retrying';
  recordsFound?: number | null;
  recordsProcessed?: number | null;
  recordsSuccessful?: number | null;
  recordsFailed?: number | null;
  recordsSkipped?: number | null;
  batchId?: string;
  jobId?: string;
  entityMappingId?: string;
  entityMappingName?: string;
  error?: string;
  output?: string;
}

export interface TaskExecutionsResponse {
  success: boolean;
  date: string;
  executions: TaskExecution[];
  count: number;
}

export interface TaskExecutionStatsResponse {
  success: boolean;
  date: string;
  stats: {
    totalExecutions: number;
    succeeded: number;
    failed: number;
    running: number;
    totalRecordsProcessed: number;
    totalDurationMs: number;
    averageDurationMs: number;
  };
}

/**
 * Get task executions for a specific date
 */
export const getExecutionsByDate = async (date: string): Promise<TaskExecutionsResponse> => {
  try {
    const response = await apiRequest("GET", `/api/task-executions?date=${date}`);
    return await response.json();
  } catch (error) {
    console.error(`Error getting executions for date ${date}:`, error);
    throw error;
  }
};

/**
 * Get task executions for a specific task
 */
export const getExecutionsByTask = async (taskId: string, days: number = 7): Promise<TaskExecutionsResponse> => {
  try {
    const response = await apiRequest("GET", `/api/task-executions/task/${taskId}?days=${days}`);
    return await response.json();
  } catch (error) {
    console.error(`Error getting executions for task ${taskId}:`, error);
    throw error;
  }
};

/**
 * Get execution statistics for a date
 */
export const getExecutionStats = async (date?: string): Promise<TaskExecutionStatsResponse> => {
  try {
    const url = date ? `/api/task-executions/stats?date=${date}` : `/api/task-executions/stats`;
    const response = await apiRequest("GET", url);
    return await response.json();
  } catch (error) {
    console.error(`Error getting execution stats:`, error);
    throw error;
  }
};

export interface RunningExecutionsResponse {
  success: boolean;
  executions: TaskExecution[];
  count: number;
}

/**
 * Get currently running task executions
 */
export const getRunningExecutions = async (): Promise<RunningExecutionsResponse> => {
  try {
    const response = await apiRequest("GET", "/api/task-executions/running");
    return await response.json();
  } catch (error) {
    console.error(`Error getting running executions:`, error);
    throw error;
  }
};

/**
 * Get task executions for a date range (client-side aggregation)
 * Queries multiple dates and combines results
 */
export const getExecutionsByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<TaskExecutionsResponse & { dateRange: { start: string; end: string } }> => {
  const executions: TaskExecution[] = [];
  const currentDate = new Date(startDate);
  const end = new Date(endDate);

  // Query each date in the range
  while (currentDate <= end) {
    const dateString = currentDate.toISOString().split('T')[0];
    try {
      const response = await getExecutionsByDate(dateString);
      if (response.success && response.executions) {
        executions.push(...response.executions);
      }
    } catch (error) {
      console.warn(`Failed to get executions for ${dateString}:`, error);
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Sort by start time descending (most recent first)
  executions.sort((a, b) =>
    new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
  );

  return {
    success: true,
    date: startDate.toISOString().split('T')[0],
    executions,
    count: executions.length,
    dateRange: {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    }
  };
};

// ============================================================================
// Watermark Column Configuration API
// ============================================================================

export type SyncTier = 'CDC' | 'CDC_METADATA' | 'INCREMENTAL' | 'FULL_SMALL' | 'FULL_MEDIUM' | 'FULL_LARGE';
export type SyncFrequency = 'HOURLY' | 'DAILY';

export interface WatermarkTable {
  tableName: string;
  rowCount: number;
  oracleRowCount: number | null;
  azureRowCount: number | null;
  rowCountSyncTime: string | null;
  lastSyncDate: string | null;
  syncType: 'INCREMENTAL' | 'FULL_REFRESH';
  syncTier: SyncTier | null;
  syncFrequency: SyncFrequency | null;
  primaryKeyColumns: string | null;
  isActive: boolean;
  currentWatermarkColumn: string | null;
  recommendedWatermarkColumn: string | null;
  watermarkDataType: string | null;
  allPotentialWatermarks: string | null;
  reason: string;
  hasWatermark: boolean;
}

export interface WatermarkTablesResponse {
  success: boolean;
  tables: WatermarkTable[];
  totalTables: number;
  tablesWithWatermark: number;
  tablesMissingWatermark: number;
}

export interface TableColumn {
  columnName: string;
  dataType: string;
  nullable: boolean;
  columnId: number;
  isWatermarkCandidate: boolean;
}

export interface TableColumnsResponse {
  success: boolean;
  tableName: string;
  columns: TableColumn[];
  recommendedColumn: string | null;
  allPotentialColumns: string | null;
}

export interface TableSampleDataResponse {
  success: boolean;
  tableName: string;
  rows: Record<string, any>[];
  totalRows: number;
  columns: string[];
}

export interface UpdateWatermarkColumnRequest {
  watermarkColumn: string;
  notes?: string;
}

export interface UpdateWatermarkColumnResponse {
  success: boolean;
  message: string;
  updatedTable: {
    tableName: string;
    watermarkColumn: string;
  };
}

export interface UpdateSyncTypeResponse {
  success: boolean;
  message: string;
  updatedTable: {
    tableName: string;
    syncType: 'INCREMENTAL' | 'FULL_REFRESH';
  };
}

export interface BulkUpdateSyncTypeResponse {
  success: boolean;
  message: string;
  updatedTables: string[];
  failedTables: Array<{ tableName: string; error: string }>;
  totalUpdated: number;
  totalFailed: number;
}

export interface UpdateSyncFrequencyResponse {
  success: boolean;
  message: string;
  updatedTable: {
    tableName: string;
    syncFrequency: SyncFrequency;
  };
}

/**
 * Get all tables with missing watermark columns
 */
export const fetchWatermarkTables = async (): Promise<WatermarkTablesResponse> => {
  const res = await apiRequest("GET", "/api/adf/watermark-tables");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || "Failed to fetch watermark tables");
  }
  return res.json();
};

/**
 * Get column schema for a specific table
 */
export const fetchTableColumns = async (tableName: string): Promise<TableColumnsResponse> => {
  const res = await apiRequest("GET", `/api/adf/table-columns/${encodeURIComponent(tableName)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || `Failed to fetch columns for ${tableName}`);
  }
  return res.json();
};

/**
 * Get sample data (first 10 rows) from a table
 */
export const fetchTableSampleData = async (tableName: string, limit: number = 10): Promise<TableSampleDataResponse> => {
  const res = await apiRequest("GET", `/api/adf/table-sample-data/${encodeURIComponent(tableName)}?limit=${limit}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || `Failed to fetch sample data for ${tableName}`);
  }
  return res.json();
};

/**
 * Update watermark column for a table
 */
export const updateWatermarkColumn = async (
  tableName: string,
  watermarkColumn: string,
  notes?: string
): Promise<UpdateWatermarkColumnResponse> => {
  const res = await apiRequest("PATCH", `/api/adf/watermark-tables/${encodeURIComponent(tableName)}`, {
    watermarkColumn,
    notes
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || `Failed to update watermark for ${tableName}`);
  }
  return res.json();
};

/**
 * Update sync type for a table (INCREMENTAL or FULL_REFRESH)
 */
export const updateTableSyncType = async (
  tableName: string,
  syncType: 'INCREMENTAL' | 'FULL_REFRESH',
  notes?: string
): Promise<UpdateSyncTypeResponse> => {
  const res = await apiRequest("PUT", `/api/adf/watermark-tables/${encodeURIComponent(tableName)}/sync-type`, {
    syncType,
    notes
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || `Failed to update sync type for ${tableName}`);
  }
  return res.json();
};

/**
 * Bulk update sync type for multiple tables
 */
export const bulkUpdateSyncType = async (
  tableNames: string[],
  syncType: 'INCREMENTAL' | 'FULL_REFRESH',
  notes?: string
): Promise<BulkUpdateSyncTypeResponse> => {
  const res = await apiRequest("POST", "/api/adf/watermark-tables/bulk-sync-type", {
    tableNames,
    syncType,
    notes
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || `Failed to bulk update sync type`);
  }
  return res.json();
};

/**
 * Update sync frequency for a table (HOURLY or DAILY)
 */
export const updateSyncFrequency = async (
  tableName: string,
  syncFrequency: SyncFrequency,
  notes?: string
): Promise<UpdateSyncFrequencyResponse> => {
  const res = await apiRequest("PUT", `/api/adf/watermark-tables/${encodeURIComponent(tableName)}/sync-frequency`, {
    syncFrequency,
    notes
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.message || `Failed to update sync frequency for ${tableName}`);
  }
  return res.json();
};

// ============================================================================
// SQL Sync API Types and Functions
// ============================================================================

export interface SqlSyncEntity {
  entityType: string;
  description: string;
  enabled: boolean;
  schedule?: {
    enabled: boolean;
    cronExpression: string;
    description: string;
  };
}

// Consolidated Dashboard Types
export interface SqlSyncDashboardEntityProgress {
  total: number;
  pending: number;
  dispatched: number;
  successful: number;
  failed: number;
  deadLetter: number;
  percentComplete: number;
  status: "Active" | "Paused" | "Idle" | "NeedsAttention";
  isActive: boolean;
  lastDispatchedAt: string | null;
}

export interface SqlSyncDashboardLastScan {
  scannedAt: string;
  changesDetected: number;
  duration: number;
}

export interface SqlSyncDashboardEntity {
  entityType: string;
  displayName: string;
  enabled: boolean;
  progress: SqlSyncDashboardEntityProgress | null;
  sourceCount: number | null;
  lastScan: SqlSyncDashboardLastScan | null;
  // Field mapping info
  hasFieldMappings: boolean;
  fieldMappingCount: number;
  fieldSyncPendingCount: number;
}

export interface SqlSyncDashboardSummary {
  totalPending: number;
  totalFailed: number;
  // totalDispatched tracks all records processed (success + failed)
  totalDispatched: number;
  // totalSuccessful is kept for backward compatibility with earlier responses
  totalSuccessful?: number;
  activeJobs: number;
}

export interface SqlSyncDashboardResponse {
  success: boolean;
  cachedAt: string;
  cacheExpiresAt: string;
  entities: SqlSyncDashboardEntity[];
  summary: SqlSyncDashboardSummary;
  recentHistory: SqlSyncStatus[];
  fromCache?: boolean;
  error?: string;
}

/**
 * Fetch consolidated SQL Sync dashboard data
 * Returns all entity progress, source counts, last scan info, and recent history in one call.
 * Results are cached server-side for 60 seconds.
 */
export const fetchSqlSyncDashboard = async (): Promise<SqlSyncDashboardResponse> => {
  const res = await apiRequest("GET", "/api/sql-sync/dashboard");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SQL sync dashboard");
  }
  return res.json();
};

/**
 * Clear SQL Sync history from Redis cache
 */
export const clearSqlSyncHistory = async (): Promise<{ success: boolean; message: string }> => {
  const res = await apiRequest("DELETE", "/api/sql-sync/history");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to clear SQL sync history");
  }
  return res.json();
};

// SQL Sync Trends
export interface SqlSyncTrendPoint {
  jobId: string;
  entityType: string;
  status: string;
  startedAt: string;
  completedAt: string;
  durationMinutes: number;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
}

export interface SqlSyncTrendsResponse {
  success: boolean;
  trends: SqlSyncTrendPoint[];
  days: number;
  totalJobs: number;
}

export const fetchSqlSyncTrends = async (days: number = 7): Promise<SqlSyncTrendsResponse> => {
  const res = await apiRequest("GET", `/api/sql-sync/trends?days=${days}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SQL sync trends");
  }
  return res.json();
};

export interface SqlSyncStatus {
  jobId: string;
  entityType: string;
  environment: "Staging" | "Production";
  status: "Pending" | "InProgress" | "Completed" | "Failed" | "Cancelled";
  totalRecords: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  startedAt: string;
  completedAt?: string;
  errors?: string[];
  currentBatch?: number;
  totalBatches?: number;
}

export interface SqlSyncEntitiesResponse {
  success: boolean;
  entities: SqlSyncEntity[];
  count: number;
}

export interface SqlSyncStatusResponse {
  success: boolean;
  status: SqlSyncStatus;
}

export interface SqlSyncHistoryResponse {
  success: boolean;
  history: SqlSyncStatus[];
  count: number;
}

export interface SqlSyncStartResponse {
  success: boolean;
  jobId?: string;
  entityType?: string;
  runningJobId?: string;
  canCancel?: boolean;
  error?: string;
}

export interface SqlSyncCancelResponse {
  success: boolean;
  message?: string;
}

export interface SqlSyncLiveProgress {
  entityType: string;
  environment: "Staging" | "Production";
  total: number;
  pending: number;
  dispatched: number;
  successful: number;
  failed: number;
  percentComplete: number;
  isActive: boolean;
  status: "Active" | "Paused" | "Idle" | "NeedsAttention";
  lastDispatchedAt: string | null;
  recentErrors: string[];
}

export interface SqlSyncLiveProgressResponse {
  success: boolean;
  progress?: SqlSyncLiveProgress;
  error?: string;
}

/**
 * Fetch available SQL Sync entity configurations
 */
export const fetchSqlSyncEntities = async (): Promise<SqlSyncEntitiesResponse> => {
  const res = await apiRequest("GET", "/api/sql-sync/entities");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SQL sync entities");
  }
  return res.json();
};

/**
 * Fetch status of a SQL Sync job
 */
export const fetchSqlSyncStatus = async (jobId: string): Promise<SqlSyncStatusResponse> => {
  const res = await apiRequest("GET", `/api/sql-sync/status/${encodeURIComponent(jobId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch status for job ${jobId}`);
  }
  return res.json();
};

/**
 * Fetch SQL Sync history
 */
export const fetchSqlSyncHistory = async (limit: number = 20): Promise<SqlSyncHistoryResponse> => {
  const res = await apiRequest("GET", `/api/sql-sync/history?limit=${limit}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SQL sync history");
  }
  return res.json();
};

/**
 * Start a SQL Sync job
 */
export const startSqlSync = async (
  entityType: string,
  environment: "Staging" | "Production",
  maxRecords?: number
): Promise<SqlSyncStartResponse> => {
  const res = await apiRequest("POST", "/api/sql-sync/start", {
    entityType,
    environment,
    ...(maxRecords && { maxRecords }),
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    if (res.status === 409 && error?.canCancel) {
      return error as SqlSyncStartResponse;
    }
    throw new Error(error?.error || `Failed to start sync for ${entityType}`);
  }
  return res.json();
};

/**
 * Cancel a running SQL Sync job
 */
export const cancelSqlSync = async (jobId: string): Promise<SqlSyncCancelResponse> => {
  const res = await apiRequest("POST", `/api/sql-sync/cancel/${encodeURIComponent(jobId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to cancel job ${jobId}`);
  }
  return res.json();
};

/**
 * Fetch live sync progress from database
 */
export const fetchSqlSyncLiveProgress = async (
  entityType: string,
  environment: "Staging" | "Production"
): Promise<SqlSyncLiveProgressResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/live-progress/${encodeURIComponent(entityType)}?environment=${environment}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch live progress for ${entityType}`);
  }
  return res.json();
};

export interface SqlSyncFailedRecord {
  id: number;
  entityKey: string;
  errorMessage: string;
  failedAt: string;
  payload: Record<string, any>;
}

export interface SqlSyncFailedRecordsResponse {
  success: boolean;
  entityType?: string;
  environment?: "Staging" | "Production";
  totalFailed?: number;
  records?: SqlSyncFailedRecord[];
  error?: string;
}

/**
 * Fetch failed records with full payload details for error inspection
 */
export const fetchSqlSyncFailedRecords = async (
  entityType: string,
  environment: "Staging" | "Production",
  limit: number = 50
): Promise<SqlSyncFailedRecordsResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/failed-records/${encodeURIComponent(entityType)}?environment=${environment}&limit=${limit}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch failed records for ${entityType}`);
  }
  return res.json();
};

export interface SqlSyncResumeResponse {
  success: boolean;
  jobId?: string;
  pendingCount?: number;
  error?: string;
}

/**
 * Resume a paused SQL Sync job
 */
export const resumeSqlSync = async (
  entityType: string,
  environment: "Staging" | "Production",
  maxRecords?: number
): Promise<SqlSyncResumeResponse> => {
  const params = new URLSearchParams({ environment });
  if (maxRecords) {
    params.append("maxRecords", String(maxRecords));
  }
  const res = await apiRequest(
    "POST",
    `/api/sql-sync/resume/${encodeURIComponent(entityType)}?${params.toString()}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to resume sync for ${entityType}`);
  }
  return res.json();
};

export interface SqlSyncStopResponse {
  success: boolean;
  entityType?: string;
  jobId?: string;
  status?: "cancelled" | "stopping";
  message?: string;
  error?: string;
}

/**
 * Stop a running SQL Sync job
 */
export const stopSqlSync = async (
  entityType: string,
  environment: "Staging" | "Production"
): Promise<SqlSyncStopResponse> => {
  const res = await apiRequest(
    "POST",
    `/api/sql-sync/stop/${encodeURIComponent(entityType)}?environment=${environment}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to stop sync for ${entityType}`);
  }
  return res.json();
};

export interface SqlSyncDetectChangesResponse {
  success: boolean;
  // When queued (async), these fields are set:
  queued?: boolean;
  message?: string;
  correlationId?: string;
  entityType?: string;
  environment?: string;
  autoStartSync?: boolean;
  // When sync (legacy/scheduler), these fields are set:
  totalChanges?: number;
  newRecords?: number;
  updatedRecords?: number;
  deletedRecords?: number;
  durationMs?: number;
  error?: string;
}

/**
 * Detect changes by running the ProcessChanges stored procedure
 * This queues a background task that compares the source view against the master table
 * and populates the Changes table. Returns immediately with a correlationId.
 *
 * @param entityType - The entity type to scan (e.g., "Title", "Series", "Contract")
 * @param environment - Target environment ("Staging" or "Production")
 * @param autoStartSync - If true (default), automatically start sync after detecting changes
 * @param resetFailed - If true (default), reset failed records to pending before scanning
 */
export const detectSqlSyncChanges = async (
  entityType: string,
  environment: string = "Staging",
  autoStartSync: boolean = false,
  resetFailed: boolean = true
): Promise<SqlSyncDetectChangesResponse> => {
  const params = new URLSearchParams({
    environment,
    autoStart: String(autoStartSync),
    resetFailed: String(resetFailed),
  });
  const res = await apiRequest(
    "POST",
    `/api/sql-sync/detect-changes/${encodeURIComponent(entityType)}?${params}`
  );
  // Both 200 and 202 are valid responses
  if (!res.ok && res.status !== 202) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to detect changes for ${entityType}`);
  }
  return res.json();
};

/**
 * Reset staging tables and rescan for changes
 * This queues a background task that truncates the Changes and Master tables,
 * then runs ProcessChanges for a fresh full scan. Returns immediately with a correlationId.
 *
 * @param entityType - The entity type to reset (e.g., "Title", "Series", "Contract")
 * @param environment - Target environment ("Staging" or "Production")
 * @param autoStartSync - If true (default), automatically start sync after rescan
 */
export const resetAndRescanSqlSync = async (
  entityType: string,
  environment: string = "Staging",
  autoStartSync: boolean = false
): Promise<SqlSyncDetectChangesResponse> => {
  const params = new URLSearchParams({
    environment,
    autoStart: String(autoStartSync),
  });
  const res = await apiRequest(
    "POST",
    `/api/sql-sync/reset-and-rescan/${encodeURIComponent(entityType)}?${params}`
  );
  // Both 200 and 202 are valid responses
  if (!res.ok && res.status !== 202) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to reset and rescan ${entityType}`);
  }
  return res.json();
};

// SQL Sync Execution History (per-job progress tracking)
export interface SqlSyncExecution {
  executionId: string;
  entityType: string;
  environment: string;
  startTime: string;
  endTime?: string;
  durationMs?: number;
  status: 'running' | 'succeeded' | 'failed';
  recordsFound: number;
  recordsProcessed: number;
  recordsSuccessful: number;
  recordsFailed: number;
}

export interface SqlSyncExecutionsResponse {
  success: boolean;
  entityType: string;
  environment: string;
  days: number;
  total: number;
  executions: SqlSyncExecution[];
}

export const fetchSqlSyncExecutions = async (
  entityType: string,
  environment: string = "Staging",
  days: number = 7,
  limit: number = 20
): Promise<SqlSyncExecutionsResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/executions/${encodeURIComponent(entityType)}?environment=${environment}&days=${days}&limit=${limit}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch executions for ${entityType}`);
  }
  return res.json();
};

// ============================================================================
// SQL Sync Pipeline View - Source Count and Last Scan
// ============================================================================

export interface SqlSyncSourceCountResponse {
  success: boolean;
  entityType?: string;
  sourceCount?: number;
  sourceView?: string;
  cached?: boolean;
  error?: string;
}

export interface SqlSyncLastScanInfo {
  entityType: string;
  timestamp: string;
  changesFound: number;
  newRecords: number;
  updatedRecords: number;
  deletedRecords: number;
  durationMs: number;
}

export interface SqlSyncLastScanResponse {
  success: boolean;
  lastScan: SqlSyncLastScanInfo | null;
  error?: string;
}

export interface SqlSyncPendingDataResponse {
  success: boolean;
  entityType: string;
  environment: "Staging" | "Production";
  totalPending: number;
  limit: number;
  rows: any[];
  error?: string;
}

export interface SqlSyncErrorGroup {
  errorPattern: string;
  count: number;
  lastOccurred: string;
  sampleEntityKeys: string[];
}

export interface SqlSyncEntityErrors {
  entityType: string;
  totalFailed: number;
  totalDeadLetter: number;
  errorGroups: SqlSyncErrorGroup[];
}

export interface SqlSyncErrorSummaryResponse {
  success: boolean;
  entities: SqlSyncEntityErrors[];
  totalErrors: number;
  error?: string;
}

export interface SqlSyncErrorDetailRecord {
  entityKey: string;
  errorMessage: string;
  failedAt: string;
  attemptCount: number;
}

export interface SqlSyncErrorDetailsResponse {
  success: boolean;
  entityType: string;
  errorPattern: string;
  records: SqlSyncErrorDetailRecord[];
  error?: string;
}

/**
 * Fetch source record count from the source view
 */
export const fetchSqlSyncSourceCount = async (
  entityType: string
): Promise<SqlSyncSourceCountResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/source-count/${encodeURIComponent(entityType)}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch source count for ${entityType}`);
  }
  return res.json();
};

/**
 * Fetch last scan info for an entity
 */
export const fetchSqlSyncLastScan = async (
  entityType: string
): Promise<SqlSyncLastScanResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/last-scan/${encodeURIComponent(entityType)}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch last scan for ${entityType}`);
  }
  return res.json();
};

/**
 * Fetch pending change sample for an entity/environment
 */
export const fetchSqlSyncPendingData = async (
  entityType: string,
  environment: "Staging" | "Production",
  limit: number = 10
): Promise<SqlSyncPendingDataResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/pending-data/${encodeURIComponent(entityType)}?environment=${encodeURIComponent(environment)}&limit=${limit}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch pending data for ${entityType}`);
  }
  return res.json();
};

/**
 * Fetch error summary across all SQL Sync entities
 */
export const fetchSqlSyncErrorSummary = async (): Promise<SqlSyncErrorSummaryResponse> => {
  const res = await apiRequest("GET", "/api/sql-sync/error-summary");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch error summary");
  }
  return res.json();
};

/**
 * Fetch detailed error records for a specific entity and error pattern
 */
export const fetchSqlSyncErrorDetails = async (
  entityType: string,
  errorPattern: string,
  limit: number = 10
): Promise<SqlSyncErrorDetailsResponse> => {
  const res = await apiRequest(
    "GET",
    `/api/sql-sync/error-details/${encodeURIComponent(entityType)}?errorPattern=${encodeURIComponent(errorPattern)}&limit=${limit}`
  );
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to fetch error details for ${entityType}`);
  }
  return res.json();
};

// ============================================================================
// SQL Sync Metadata API
// ============================================================================

export interface SqlSyncEntityMetadata {
  entityType: string;
  sourceViewCount: number | null;
  sourceViewCountedAt: string | null;
  masterTotal: number | null;
  masterPending: number | null;
  masterDispatched: number | null;
  masterFailed: number | null;
  masterDeadLetter: number | null;
  masterCountedAt: string | null;
  previewNewRecords: number | null;
  previewUpdatedRecords: number | null;
  previewDeletedRecords: number | null;
  previewTotalChanges: number | null;
  previewScannedAt: string | null;
  previewDurationMs: number | null;
  largeChangeThreshold: number;
  lastRefreshedAt: string | null;
  lastRefreshDurationMs: number | null;
}

export interface SqlSyncMetadataResponse {
  success: boolean;
  metadata: SqlSyncEntityMetadata[];
  count: number;
  error?: string;
}

export interface SqlSyncRefreshMetadataResult {
  entityType: string;
  success: boolean;
  sourceViewCount?: number;
  masterTotal?: number;
  previewTotalChanges?: number;
  durationMs?: number;
  error?: string;
}

export interface SqlSyncRefreshMetadataResponse {
  success: boolean;
  results: SqlSyncRefreshMetadataResult[];
  totalDurationMs: number;
  error?: string;
}

/**
 * Fetch cached entity sync metadata from database
 * Returns source counts, master table status, and preview scan results
 */
export const fetchSqlSyncMetadata = async (): Promise<SqlSyncMetadataResponse> => {
  const res = await apiRequest("GET", "/api/sql-sync/metadata");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch SQL sync metadata");
  }
  return res.json();
};

/**
 * Refresh entity sync metadata (trigger immediate scan)
 * @param entityType - Optional specific entity to refresh (if not provided, refreshes all)
 * @param includePreview - Whether to include preview scan (default: false)
 */
export const refreshSqlSyncMetadata = async (
  entityType?: string,
  includePreview: boolean = false
): Promise<SqlSyncRefreshMetadataResponse> => {
  const params = new URLSearchParams({ includePreview: String(includePreview) });
  const url = entityType
    ? `/api/sql-sync/refresh-metadata/${encodeURIComponent(entityType)}?${params}`
    : `/api/sql-sync/refresh-metadata?${params}`;
  const res = await apiRequest("POST", url);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to refresh SQL sync metadata");
  }
  return res.json();
};

// ============================================================================
// Activity Log API
// ============================================================================

export type ActivityCategory = 'SqlSync' | 'EntityMapping' | 'FieldMapping' | 'SharePoint' | 'Scheduled';
export type ActivityStatus = 'success' | 'failed' | 'queued';

export interface ActivityLog {
  activityId?: number;
  action: string;
  category: ActivityCategory;
  entityType?: string;
  userId?: string;
  userEmail?: string;
  userDisplayName?: string;
  ipAddress?: string;
  userAgent?: string;
  jobId?: string;
  batchNumber?: number;
  correlationId?: string;
  taskDefinitionId?: string;
  executionId?: string;
  status: ActivityStatus;
  durationMs?: number;
  recordsAffected?: number;
  requestParams?: Record<string, unknown>;
  responseData?: Record<string, unknown>;
  errorMessage?: string;
  timestamp: string;
}

export interface ActivityFilter {
  category?: ActivityCategory;
  entityType?: string;
  action?: string;
  status?: ActivityStatus;
  userId?: string;
  jobId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface ActivityQueryResponse {
  success: boolean;
  activities: ActivityLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface ActivitySummary {
  totalActivities: number;
  successCount: number;
  failedCount: number;
  successRate: number;
  uniqueUsers: number;
  uniqueEntities: number;
  byAction: Record<string, number>;
  byCategory: Record<string, number>;
  byStatus: Record<string, number>;
  periodDays: number;
}

export interface ActivitySummaryResponse {
  success: boolean;
  summary: ActivitySummary;
}

/**
 * Query activity log entries with optional filters
 */
export const queryActivityLog = async (
  filter?: ActivityFilter
): Promise<ActivityQueryResponse> => {
  const params = new URLSearchParams();

  if (filter) {
    if (filter.category) params.append('category', filter.category);
    if (filter.entityType) params.append('entityType', filter.entityType);
    if (filter.action) params.append('action', filter.action);
    if (filter.status) params.append('status', filter.status);
    if (filter.userId) params.append('userId', filter.userId);
    if (filter.jobId) params.append('jobId', filter.jobId);
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.limit !== undefined) params.append('limit', filter.limit.toString());
    if (filter.offset !== undefined) params.append('offset', filter.offset.toString());
  }

  const url = `/api/activity-log${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await apiRequest("GET", url);

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to query activity log");
  }

  return res.json();
};

/**
 * Fetch activity summary statistics
 */
export const fetchActivitySummary = async (
  days: number = 7
): Promise<ActivitySummaryResponse> => {
  const url = `/api/activity-log/summary?days=${days}`;
  const res = await apiRequest("GET", url);

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch activity summary");
  }

  return res.json();
};

// ============================================================================
// Field Mapping Sync API (Batch Field Sync)
// ============================================================================

export interface FieldSyncStatus {
  entityType: string;
  jobId?: string;
  status: 'Idle' | 'Running' | 'Completed' | 'Failed' | 'Paused' | 'Stopped' | 'Cancelled';
  startedAt?: string;
  completedAt?: string;
  totalRecords?: number;
  totalChunks?: number;
  processedRecords?: number;
  successfulRecords?: number;
  failedRecords?: number;
  skippedRecords?: number;
  batchesCompleted?: number;
  isPaused?: boolean;
  error?: string;
}

export interface FieldSyncStartResponse {
  success: boolean;
  entityType: string;
  jobId?: string;
  totalRecords?: number;
  totalChunks?: number;
  processed?: number;
  succeeded?: number;
  failed?: number;
  batchCount?: number;
  durationMs?: number;
  message?: string;
  error?: string;
}

export interface FieldSyncStatusResponse {
  entityType: string;
  isRunning: boolean;
  pendingCount: number;
  currentJob: FieldSyncStatus | null;
}

export interface FieldSyncAllStatusResponse {
  [entityType: string]: {
    isRunning: boolean;
    isPaused: boolean;
    pendingCount: number;
    mappingCount: number;
    hasMappings: boolean;
    currentJob: FieldSyncStatus | null;
  };
}

export interface FieldSyncFlagAllResponse {
  entityType: string;
  flaggedCount: number;
  message: string;
}

/**
 * Start field mapping sync for an entity type
 */
export const startFieldSync = async (
  entityType: string,
  maxRecords?: number,
  batchSize?: number
): Promise<FieldSyncStartResponse> => {
  const params = new URLSearchParams();
  if (maxRecords) params.append('maxRecords', String(maxRecords));
  if (batchSize) params.append('batchSize', String(batchSize));

  const url = `/api/field-sync/start/${encodeURIComponent(entityType)}${params.toString() ? `?${params.toString()}` : ''}`;
  const res = await apiRequest("POST", url);

  if (!res.ok && res.status !== 409) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to start field sync for ${entityType}`);
  }
  return res.json();
};

/**
 * Get field sync status for an entity type
 */
export const getFieldSyncStatus = async (entityType: string): Promise<FieldSyncStatusResponse> => {
  const res = await apiRequest("GET", `/api/field-sync/status/${encodeURIComponent(entityType)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to get field sync status for ${entityType}`);
  }
  return res.json();
};

/**
 * Get field sync status for all entity types
 */
export const getAllFieldSyncStatus = async (): Promise<FieldSyncAllStatusResponse> => {
  const res = await apiRequest("GET", "/api/field-sync/status");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to get all field sync status");
  }
  return res.json();
};

/**
 * Pause field sync for an entity type
 */
export const pauseFieldSync = async (entityType: string): Promise<{ message: string }> => {
  const res = await apiRequest("POST", `/api/field-sync/pause/${encodeURIComponent(entityType)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to pause field sync for ${entityType}`);
  }
  return res.json();
};

/**
 * Resume field sync for an entity type (clear pause flag)
 */
export const resumeFieldSync = async (entityType: string): Promise<{ message: string }> => {
  const res = await apiRequest("POST", `/api/field-sync/resume/${encodeURIComponent(entityType)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to resume field sync for ${entityType}`);
  }
  return res.json();
};

/**
 * Flag all records for field sync (backfill)
 */
export const flagAllForFieldSync = async (entityType: string): Promise<FieldSyncFlagAllResponse> => {
  const res = await apiRequest("POST", `/api/field-sync/flag-all/${encodeURIComponent(entityType)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to flag all records for ${entityType}`);
  }
  return res.json();
};

/**
 * Stop/cancel field sync for an entity type
 */
export const stopFieldSync = async (entityType: string): Promise<{ message: string }> => {
  const res = await apiRequest("POST", `/api/field-sync/stop/${encodeURIComponent(entityType)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to stop field sync for ${entityType}`);
  }
  return res.json();
};

export interface FieldSyncHistoryItem {
  jobId: string;
  entityType: string;
  status: string;
  totalRecords: number;
  totalChunks: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  skippedRecords: number;
  startedAt: string;
  completedAt?: string;
  maxRecords?: number;
}

/**
 * Get field sync job history
 */
export const getFieldSyncHistory = async (limit?: number): Promise<{ history: FieldSyncHistoryItem[]; count: number }> => {
  const params = limit ? `?limit=${limit}` : '';
  const res = await apiRequest("GET", `/api/field-sync/history${params}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to get field sync history");
  }
  return res.json();
};

// ============================================================================
// Scheduler Dashboard API (SQL-backed)
// ============================================================================

export interface SchedulerExecutionStats {
  totalExecutions: number;
  succeeded: number;
  failed: number;
  running: number;
  successRate: number;
  avgDurationMs: number;
}

export interface SchedulerDashboardSummary {
  totalTasks: number;
  parentTasks: number;
  enabledTasks: number;
  tasksByGroup: Record<string, number>;
  executionsLast24h: number;
  successRate: number;
}

export interface SchedulerTaskSummary {
  id: string;
  name: string;
  nextExecutionTime?: string;
  lastExecutionTime?: string;
  taskGroup?: string;
}

export interface SchedulerExecutionRecord {
  id: number;
  executionId: string;
  taskId: string;
  taskName?: string;
  parentExecutionId?: string;
  jobId?: string;
  jobType?: string;
  startTime: string;
  endTime?: string;
  durationMs?: number;
  status: 'running' | 'succeeded' | 'failed' | 'skipped' | 'cancelled';
  recordsFound?: number;
  recordsProcessed?: number;
  recordsSuccessful?: number;
  recordsFailed?: number;
  recordsSkipped?: number;
  error?: string;
}

export interface SchedulerDashboardResponse {
  success: boolean;
  dashboard: {
    summary: SchedulerDashboardSummary;
    stats: SchedulerExecutionStats;
    upcomingTasks: SchedulerTaskSummary[];
    failedTasks: SchedulerTaskSummary[];
    recentExecutions: SchedulerExecutionRecord[];
  };
  error?: string;
}

export interface SchedulerExecutionsResponse {
  success: boolean;
  executions: SchedulerExecutionRecord[];
  count: number;
  error?: string;
}

export interface SchedulerTaskHistoryResponse {
  success: boolean;
  taskId: string;
  executions: SchedulerExecutionRecord[];
  count: number;
  error?: string;
}

export interface SchedulerChildExecutionsResponse {
  success: boolean;
  parentExecutionId: string;
  children: SchedulerExecutionRecord[];
  count: number;
  error?: string;
}

export interface SchedulerGroupResponse {
  success: boolean;
  groupName: string;
  tasks: ScheduledTask[];
  count: number;
  error?: string;
}

export interface SchedulerGroupActionResponse {
  success: boolean;
  message: string;
  count: number;
  error?: string;
}

/**
 * Get scheduler dashboard overview
 */
export const getSchedulerDashboard = async (days: number = 7): Promise<SchedulerDashboardResponse> => {
  const res = await apiRequest("GET", `/api/scheduler/dashboard?days=${days}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || 'Failed to get scheduler dashboard');
  }
  return res.json();
};

/**
 * Get recent executions across all tasks
 */
export const getSchedulerExecutions = async (limit: number = 20): Promise<SchedulerExecutionsResponse> => {
  const res = await apiRequest("GET", `/api/scheduler/executions?limit=${limit}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || 'Failed to get scheduler executions');
  }
  return res.json();
};

/**
 * Get execution history for a specific task
 */
export const getSchedulerTaskHistory = async (taskId: string, limit: number = 10): Promise<SchedulerTaskHistoryResponse> => {
  const res = await apiRequest("GET", `/api/scheduler/tasks/${encodeURIComponent(taskId)}/history?limit=${limit}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to get history for task ${taskId}`);
  }
  return res.json();
};

/**
 * Get child executions for a parent execution
 */
export const getSchedulerChildExecutions = async (executionId: string): Promise<SchedulerChildExecutionsResponse> => {
  const res = await apiRequest("GET", `/api/scheduler/executions/${encodeURIComponent(executionId)}/children`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to get children for execution ${executionId}`);
  }
  return res.json();
};

/**
 * Get tasks in a group
 */
export const getTasksInGroup = async (groupName: string): Promise<SchedulerGroupResponse> => {
  const res = await apiRequest("GET", `/api/scheduler/groups/${encodeURIComponent(groupName)}/tasks`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to get tasks in group ${groupName}`);
  }
  return res.json();
};

/**
 * Pause all tasks in a group
 */
export const pauseTaskGroup = async (groupName: string): Promise<SchedulerGroupActionResponse> => {
  const res = await apiRequest("POST", `/api/scheduler/groups/${encodeURIComponent(groupName)}/pause`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to pause group ${groupName}`);
  }
  return res.json();
};

/**
 * Resume all tasks in a group
 */
export const resumeTaskGroup = async (groupName: string): Promise<SchedulerGroupActionResponse> => {
  const res = await apiRequest("POST", `/api/scheduler/groups/${encodeURIComponent(groupName)}/resume`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to resume group ${groupName}`);
  }
  return res.json();
};

/**
 * Get child tasks for a parent task
 */
export const getScheduledTaskChildren = async (taskId: string): Promise<{ success: boolean; parentTaskId: string; children: ScheduledTask[]; count: number }> => {
  const res = await apiRequest("GET", `/api/scheduledTasks/${encodeURIComponent(taskId)}/children`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to get children for task ${taskId}`);
  }
  return res.json();
};

/**
 * Add a child task to a parent
 */
export const addScheduledTaskChild = async (parentTaskId: string, childData: Partial<ScheduledTask>): Promise<{ success: boolean; child: ScheduledTask }> => {
  const res = await apiRequest("POST", `/api/scheduledTasks/${encodeURIComponent(parentTaskId)}/children`, childData);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to add child to task ${parentTaskId}`);
  }
  return res.json();
};

/**
 * Reorder child tasks
 */
export const reorderScheduledTaskChildren = async (parentTaskId: string, orderedTaskIds: string[]): Promise<{ success: boolean; message: string }> => {
  const res = await apiRequest("PUT", `/api/scheduledTasks/${encodeURIComponent(parentTaskId)}/children/order`, { orderedTaskIds });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to reorder children for task ${parentTaskId}`);
  }
  return res.json();
};

// ============================================================================
// Record Merge API
// ============================================================================

export interface MergeSearchResult {
  id: string;
  name: string;
  titleId: string | null;  // pl_kup_title_id is a String field
  projectNo: string | null;
  createdOn: string;
  modifiedOn: string;
}

export interface MergeSearchResponse {
  results: MergeSearchResult[];
  total: number;
}

export interface MergeFieldComparison {
  field: string;
  fieldDisplayName: string;
  valueA: any;
  valueB: any;
  isDifferent: boolean;
  isLookup: boolean; // Lookup fields require special handling and can't be directly merged
}

export interface MergeCompareResponse {
  recordA: Record<string, any>;
  recordB: Record<string, any>;
  differences: MergeFieldComparison[];
  allFields: MergeFieldComparison[];
}

export interface MergeAssociatedRecord {
  id: string;
  name: string;
  entityType: string;
  lookupField: string;
}

export interface MergeAssociationsResponse {
  contracts: MergeAssociatedRecord[];
  products: MergeAssociatedRecord[];
  series: MergeAssociatedRecord[];
}

export interface MergePreviewResponse {
  winnerId: string;
  loserId: string;
  fieldsToUpdate: { field: string; oldValue: any; newValue: any }[];
  associationsToMove: {
    contracts: number;
    products: number;
    series: number;
  };
  warnings: string[];
}

export interface MergeExecuteResponse {
  success: boolean;
  mergedRecordId: string;
  fieldsUpdated: number;
  associationsMoved: {
    contracts: number;
    products: number;
    series: number;
  };
  deletedRecordId: string;
  error?: string;
}

/**
 * Search for titles to merge
 */
export const searchMergeTitles = async (
  searchType: "name" | "titleId" | "projectNo",
  value: string,
  limit: number = 50
): Promise<MergeSearchResponse> => {
  const params = new URLSearchParams({
    type: searchType,
    value,
    limit: String(limit),
  });
  const res = await apiRequest("GET", `/api/merge/search?${params.toString()}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to search for titles");
  }
  return res.json();
};

/**
 * Compare two records for merging
 */
export const compareMergeRecords = async (
  recordA: string,
  recordB: string
): Promise<MergeCompareResponse> => {
  const params = new URLSearchParams({ recordA, recordB });
  const res = await apiRequest("GET", `/api/merge/compare?${params.toString()}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to compare records");
  }
  return res.json();
};

/**
 * Get associated records for a title
 */
export const getMergeAssociations = async (
  recordId: string
): Promise<MergeAssociationsResponse> => {
  const res = await apiRequest("GET", `/api/merge/associations/${encodeURIComponent(recordId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to get associations");
  }
  return res.json();
};

/**
 * Preview a merge operation
 */
export const previewMerge = async (
  winnerId: string,
  loserId: string,
  fieldsToMerge: string[]
): Promise<MergePreviewResponse> => {
  const res = await apiRequest("POST", "/api/merge/preview", {
    winnerId,
    loserId,
    fieldsToMerge,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to preview merge");
  }
  return res.json();
};

/**
 * Execute a merge operation
 */
export const executeMerge = async (
  winnerId: string,
  loserId: string,
  fieldsToMerge: string[]
): Promise<MergeExecuteResponse> => {
  const res = await apiRequest("POST", "/api/merge/execute", {
    winnerId,
    loserId,
    fieldsToMerge,
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to execute merge");
  }
  return res.json();
};

// ============================================================================
// Sentiment Analysis API
// ============================================================================

import type {
  SentimentDashboardStats,
  SentimentComplaintsResponse,
  SentimentTicket,
  SentimentEmail,
  SentimentConfig,
  SentimentCostSummary,
} from "./sentiment-types";

export const fetchSentimentDashboard = async (
  pipelineId?: string
): Promise<SentimentDashboardStats & { success: boolean }> => {
  const params = pipelineId ? `?pipelineId=${pipelineId}` : "";
  const res = await apiRequest("GET", `/api/sentiment/dashboard${params}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch sentiment dashboard");
  }
  return res.json();
};

export const fetchSentimentComplaints = async (
  filters: {
    page?: number;
    pageSize?: number;
    severity?: number;
    category?: string;
    from?: string;
    to?: string;
    search?: string;
  } = {}
): Promise<SentimentComplaintsResponse & { success: boolean }> => {
  const params = new URLSearchParams();
  if (filters.page) params.set("page", filters.page.toString());
  if (filters.pageSize) params.set("pageSize", filters.pageSize.toString());
  if (filters.severity !== undefined) params.set("severity", filters.severity.toString());
  if (filters.category) params.set("category", filters.category);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.search) params.set("search", filters.search);
  const qs = params.toString();
  const res = await apiRequest("GET", `/api/sentiment/complaints${qs ? `?${qs}` : ""}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch complaints");
  }
  return res.json();
};

export const fetchSentimentTicket = async (
  ticketId: string
): Promise<{ success: boolean; ticket: SentimentTicket; emails: (SentimentEmail)[] }> => {
  const res = await apiRequest("GET", `/api/sentiment/tickets/${encodeURIComponent(ticketId)}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch ticket");
  }
  return res.json();
};

export const fetchSentimentConfig = async (): Promise<{
  success: boolean;
  configs: SentimentConfig[];
}> => {
  const res = await apiRequest("GET", "/api/sentiment/config");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch config");
  }
  return res.json();
};

export const updateSentimentConfig = async (
  pipelineId: string,
  updates: Partial<SentimentConfig>
): Promise<{ success: boolean }> => {
  const res = await apiRequest("PUT", `/api/sentiment/config/${encodeURIComponent(pipelineId)}`, updates);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to update config");
  }
  return res.json();
};

export const triggerSentimentExtraction = async (): Promise<{ success: boolean; results: any[] }> => {
  const res = await apiRequest("POST", "/api/sentiment/extract");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to trigger extraction");
  }
  return res.json();
};

export const triggerSentimentAnalysis = async (): Promise<{
  success: boolean;
  jobId: string;
  emailsQueued: number;
}> => {
  const res = await apiRequest("POST", "/api/sentiment/analyse");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to trigger analysis");
  }
  return res.json();
};

export const fetchSentimentCostSummary = async (): Promise<
  SentimentCostSummary & { success: boolean }
> => {
  const res = await apiRequest("GET", "/api/sentiment/cost-summary");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch cost summary");
  }
  return res.json();
};

export const discoverSentimentPipelines = async (): Promise<{
  success: boolean;
  discovered: number;
  pipelines: { pipelineId: string; label: string; stageCount: number }[];
  configs: SentimentConfig[];
}> => {
  const res = await apiRequest("POST", "/api/sentiment/discover-pipelines");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to discover pipelines");
  }
  return res.json();
};

export const deleteSentimentConfig = async (pipelineId: string): Promise<void> => {
  const res = await apiRequest("DELETE", `/api/sentiment/config/${pipelineId}`);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to delete pipeline config");
  }
};

export const fetchSentimentDefaultPrompt = async (): Promise<{ prompt: string }> => {
  const res = await apiRequest("GET", "/api/sentiment/default-prompt");
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch default prompt");
  }
  return res.json();
};

// ============================================================================
// ADF Health Monitor & Guardian Overview
// ============================================================================

export interface AdfHealthCheckState {
  checkType: string;
  status: "healthy" | "warning" | "critical";
  lastCheckedAt: string | null;
  lastAlertedAt: string | null;
  lastAlertSummary: string | null;
  consecutiveFailures: number;
}

export interface SystemHealthLayer {
  name: string;
  description: string;
  status: "healthy" | "warning" | "critical";
  checks?: AdfHealthCheckState[];
  states?: any[];
}

export interface SystemHealthOverviewResponse {
  success: boolean;
  overallStatus: "healthy" | "warning" | "critical";
  layers: SystemHealthLayer[];
}

export interface AdfHealthReport {
  overallStatus: "healthy" | "warning" | "critical";
  checks: Array<{
    checkType: string;
    status: "healthy" | "warning" | "critical";
    summary: string;
    details: any;
    affectedTables?: string[];
    checkedAt: string;
  }>;
  generatedAt: string;
  durationMs: number;
}

export const fetchSystemHealthOverview = async (): Promise<SystemHealthOverviewResponse> => {
  const res = await apiRequest("GET", "/api/guardian/overview");
  if (!res.ok) {
    throw new Error("Failed to fetch system health overview");
  }
  return res.json();
};

export const fetchAdfHealthStatus = async (): Promise<{
  success: boolean;
  states: AdfHealthCheckState[];
  recentRuns: any[];
  overallStatus: "healthy" | "warning" | "critical";
}> => {
  const res = await apiRequest("GET", "/api/adf/health/status");
  if (!res.ok) {
    throw new Error("Failed to fetch ADF health status");
  }
  return res.json();
};

export const triggerAdfHealthEvaluate = async (): Promise<{ success: boolean; report: AdfHealthReport }> => {
  const res = await apiRequest("POST", "/api/adf/health/evaluate");
  if (!res.ok) {
    throw new Error("Failed to trigger ADF health evaluation");
  }
  return res.json();
};

export const acknowledgeAdfAlert = async (checkType: string): Promise<void> => {
  const res = await apiRequest("POST", `/api/adf/health/acknowledge/${checkType}`);
  if (!res.ok) {
    throw new Error("Failed to acknowledge ADF health alert");
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// OP SYNC API
// ═══════════════════════════════════════════════════════════════════════════════

export interface OPSyncDashboardStats {
  entityType: string;
  totalRecords: number;
  pending: number;
  dispatched: number;
  deadLetter: number;
  skipped: number;
  audited: number;
  totalMismatches: number;
  notFoundInOP: number;
  lastSyncedAt: string | null;
  lastAuditAt: string | null;
}

export interface OPSyncFieldComparison {
  field: string;
  expected: unknown;
  actual: unknown;
  match: boolean;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  notes?: string;
}

export interface OPSyncAuditResult {
  entityKey: string;
  ean: string;
  opProductId: number | null;
  notFoundInOP: boolean;
  comparisons: OPSyncFieldComparison[];
  mismatchCount: number;
  timestamp: string;
  parseError?: boolean;
}

export interface OPSyncAuditFieldSummary {
  fieldName: string;
  severity: string;
  mismatchCount: number;
  sampleEntityKey: string;
  sampleExpected: string | null;
  sampleActual: string | null;
}

export interface OPSyncDashboardResponse {
  success: boolean;
  entities: string[];
  stats: OPSyncDashboardStats[];
}

export interface OPSyncJobStatus {
  jobId: string;
  entityType: string;
  status: string;
  dryRun: boolean;
  totalRecords: number;
  totalChunks: number;
  processedRecords: number;
  successfulRecords: number;
  failedRecords: number;
  skippedRecords: number;
  startedAt: string;
  completedAt?: string;
  auditRunId?: string;
  triggerSource?: 'manual' | 'scheduler';
}

export interface OPSyncAuditRun {
  AuditRunId: string;
  EntityType: string;
  LookbackDays: number;
  SinceDate: string;
  UntilDate: string;
  Status: string;
  TriggerSource: 'manual' | 'scheduler';
  DetectionTotal: number;
  DetectionNew: number;
  DetectionUpdated: number;
  DetectionUnchanged: number;
  AuditJobId: string | null;
  InSyncCount: number;
  MismatchCount: number;
  MissingCount: number;
  FailedCount: number;
  SkippedCount: number;
  StartedAt: string;
  DetectionCompletedAt: string | null;
  AuditCompletedAt: string | null;
  CancelledAt: string | null;
  LastError: string | null;
}

export interface OPSyncMasterRecord {
  EntityType: string;
  EntityKey: string;
  EAN: string | null;
  OPProductId: number | null;
  PayloadJson: string | null;
  RowHash: string | null;
  IsDispatched: boolean;
  AttemptCount: number;
  LastError: string | null;
  SkipReason: string | null;
  LastAuditResult: string | null;
  LastAuditAt: string | null;
  AuditMismatchCount: number;
  IsDeadLetter: boolean;
  DeadLetterReason: string | null;
}

export interface OPSyncAuditRunItem {
  AuditRunId: string;
  EntityType: string;
  EntityKey: string;
  EAN: string | null;
  SourceModifiedOn: string | null;
  DetectionAction: 'INSERT' | 'UPDATE' | 'UNCHANGED';
  AuditStatus: 'IN_SYNC' | 'MISMATCH' | 'MISSING_IN_OP' | 'FAILED' | 'SKIPPED' | 'PENDING';
  MismatchCount: number;
  NotFoundInOP: boolean;
  OPProductId: number | null;
  LastError: string | null;
  SkipReason: string | null;
  AuditResultJson: string | null;
  CreatedAt: string;
  UpdatedAt: string;
}

export const fetchOPSyncDashboard = async (): Promise<OPSyncDashboardResponse> => {
  const res = await apiRequest("GET", "/api/op-sync/dashboard");
  if (!res.ok) throw new Error("Failed to fetch OP Sync dashboard");
  return res.json();
};

export const startOPSyncDryRun = async (
  entityType: string,
  maxRecords?: number
): Promise<{ success: boolean; jobId: string; totalRecords: number; totalChunks: number }> => {
  const params = maxRecords ? `?maxRecords=${maxRecords}` : "";
  const res = await apiRequest("POST", `/api/op-sync/audit/${entityType}${params}`);
  if (!res.ok) throw new Error("Failed to start OP Sync dry run");
  return res.json();
};

export const startOPSync = async (
  entityType: string,
  maxRecords?: number
): Promise<{ success: boolean; jobId: string; totalRecords: number; totalChunks: number }> => {
  const params = maxRecords ? `?maxRecords=${maxRecords}` : "";
  const res = await apiRequest("POST", `/api/op-sync/start/${entityType}${params}`);
  if (!res.ok) throw new Error("Failed to start OP Sync");
  return res.json();
};

export const stopOPSync = async (entityType: string): Promise<void> => {
  const res = await apiRequest("POST", `/api/op-sync/stop/${entityType}`);
  if (!res.ok) throw new Error("Failed to stop OP Sync");
};

export const detectOPSyncChanges = async (
  entityType: string,
  sinceDate?: string
): Promise<{ success: boolean; totalProcessed: number; newRecords: number; updatedRecords: number; unchangedRecords: number }> => {
  const params = new URLSearchParams();
  if (sinceDate) params.set("sinceDate", sinceDate);
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiRequest("POST", `/api/op-sync/detect-changes/${entityType}${qs}`);
  if (!res.ok) throw new Error("Failed to detect OP Sync changes");
  return res.json();
};

export const startOPSyncAuditWindow = async (
  entityType: string,
  days: number,
  maxRecords?: number
): Promise<{
  success: boolean;
  auditRunId: string;
  sinceDate: string;
  untilDate: string;
  detection: {
    totalProcessed: number;
    newRecords: number;
    updatedRecords: number;
    unchangedRecords: number;
  };
  job: { jobId: string; totalRecords: number; totalChunks: number };
}> => {
  const params = new URLSearchParams({ days: String(days) });
  if (maxRecords) params.set("maxRecords", String(maxRecords));
  const res = await apiRequest("POST", `/api/op-sync/audit-window/${entityType}?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to start OP Sync audit window");
  return res.json();
};

export const startOPSyncAuditDetectOnly = async (
  entityType: string,
  days: number,
  maxRecords?: number
): Promise<{
  success: boolean;
  auditRunId: string;
  sinceDate: string;
  untilDate: string;
  detection: {
    totalProcessed: number;
    newRecords: number;
    updatedRecords: number;
    unchangedRecords: number;
  };
  job: { jobId: string; totalRecords: number; totalChunks: number };
}> => {
  const params = new URLSearchParams({ days: String(days), detectOnly: "true" });
  if (maxRecords) params.set("maxRecords", String(maxRecords));
  const res = await apiRequest("POST", `/api/op-sync/audit-window/${entityType}?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to detect OP Sync changes");
  return res.json();
};

export const dispatchOPSyncAuditRun = async (
  auditRunId: string,
  entityKeys?: string[]
): Promise<{ success: boolean; jobId: string; totalRecords: number; totalChunks: number }> => {
  const res = await apiRequest(
    "POST",
    `/api/op-sync/audit-runs/${auditRunId}/dispatch`,
    entityKeys && entityKeys.length > 0 ? { entityKeys } : undefined
  );
  if (!res.ok) throw new Error("Failed to dispatch OP Sync audit run");
  return res.json();
};

export const getOPSyncStatus = async (
  jobId: string
): Promise<{ success: boolean; status: OPSyncJobStatus }> => {
  const res = await apiRequest("GET", `/api/op-sync/status/${jobId}`);
  if (!res.ok) throw new Error("Failed to get OP Sync status");
  return res.json();
};

export const getOPSyncAuditResults = async (
  entityType: string,
  limit?: number,
  offset?: number
): Promise<{ success: boolean; records: OPSyncMasterRecord[]; total: number }> => {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  if (offset) params.set("offset", String(offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiRequest("GET", `/api/op-sync/audit-results/${entityType}${qs}`);
  if (!res.ok) throw new Error("Failed to get OP Sync audit results");
  return res.json();
};

export const getOPSyncAuditSummary = async (
  entityType: string
): Promise<{ success: boolean; summary: OPSyncAuditFieldSummary[] }> => {
  const res = await apiRequest("GET", `/api/op-sync/audit-summary/${entityType}`);
  if (!res.ok) throw new Error("Failed to get OP Sync audit summary");
  return res.json();
};

export const getOPSyncFailedRecords = async (
  entityType: string,
  limit?: number
): Promise<{ success: boolean; records: OPSyncMasterRecord[] }> => {
  const params = limit ? `?limit=${limit}` : "";
  const res = await apiRequest("GET", `/api/op-sync/failed-records/${entityType}${params}`);
  if (!res.ok) throw new Error("Failed to get OP Sync failed records");
  return res.json();
};

export const getOPSyncSkippedRecords = async (
  entityType: string,
  limit?: number
): Promise<{ success: boolean; records: OPSyncMasterRecord[] }> => {
  const params = limit ? `?limit=${limit}` : "";
  const res = await apiRequest("GET", `/api/op-sync/skipped/${entityType}${params}`);
  if (!res.ok) throw new Error("Failed to get OP Sync skipped records");
  return res.json();
};

export const getOPSyncHistory = async (
  limit?: number
): Promise<{ success: boolean; history: OPSyncJobStatus[] }> => {
  const params = limit ? `?limit=${limit}` : "";
  const res = await apiRequest("GET", `/api/op-sync/history${params}`);
  if (!res.ok) throw new Error("Failed to get OP Sync history");
  return res.json();
};

export const listOPSyncAuditRuns = async (
  entityType: string,
  limit?: number
): Promise<{ success: boolean; runs: OPSyncAuditRun[] }> => {
  const params = new URLSearchParams({ entityType });
  if (limit) params.set("limit", String(limit));
  const res = await apiRequest("GET", `/api/op-sync/audit-runs?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to get OP Sync audit runs");
  return res.json();
};

export const getOPSyncAuditRun = async (
  auditRunId: string
): Promise<{
  success: boolean;
  run: OPSyncAuditRun | null;
  totals: {
    changedInDataverse: number;
    inSync: number;
    mismatched: number;
    missingInOP: number;
    failed: number;
    skipped: number;
  };
}> => {
  const res = await apiRequest("GET", `/api/op-sync/audit-runs/${auditRunId}`);
  if (!res.ok) throw new Error("Failed to get OP Sync audit run");
  return res.json();
};

export const getOPSyncAuditRunFieldSummary = async (
  auditRunId: string
): Promise<{ success: boolean; summary: OPSyncAuditFieldSummary[] }> => {
  const res = await apiRequest("GET", `/api/op-sync/audit-runs/${auditRunId}/field-summary`);
  if (!res.ok) throw new Error("Failed to get OP Sync audit run field summary");
  return res.json();
};

export const getOPSyncAuditRunResults = async (
  auditRunId: string,
  status?: string,
  limit?: number,
  offset?: number
): Promise<{ success: boolean; records: OPSyncAuditRunItem[]; total: number }> => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (limit) params.set("limit", String(limit));
  if (offset) params.set("offset", String(offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiRequest("GET", `/api/op-sync/audit-runs/${auditRunId}/results${qs}`);
  if (!res.ok) throw new Error("Failed to get OP Sync audit run results");
  return res.json();
};

export interface UserApiKeyMetadata {
  apiKeyId: string;
  userId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
  createdBy?: string | null;
  revokedAt?: string | null;
  revokedBy?: string | null;
}

export const listUserApiKeys = async (userId: string): Promise<UserApiKeyMetadata[]> => {
  const res = await apiRequest("GET", `/api/users/${encodeURIComponent(userId)}/api-keys`);
  const data = await res.json();
  if (!res.ok) throw new Error(data?.message || "Failed to list API keys");
  return data.apiKeys || [];
};

export const createUserApiKey = async (
  userId: string,
  input: { name: string; scopes: string[]; expiresAt?: string | null }
): Promise<UserApiKeyMetadata & { secret: string }> => {
  const res = await apiRequest("POST", `/api/users/${encodeURIComponent(userId)}/api-keys`, input);
  const data = await res.json();
  if (!res.ok) throw new Error(data?.message || "Failed to create API key");
  return data.apiKey;
};

export const revokeUserApiKey = async (userId: string, apiKeyId: string): Promise<void> => {
  const res = await apiRequest("DELETE", `/api/users/${encodeURIComponent(userId)}/api-keys/${encodeURIComponent(apiKeyId)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || "Failed to revoke API key");
};
